import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './SuperAdminStyles.css';
import {
  Users, UserPlus, PieChart, Building, CreditCard, Package as PackageIcon, Ticket, Settings as SettingsIcon, Mail,
  Search, Plus, Edit, Trash2, Check, X, Shield, Lock, Play, Pause,
  DollarSign, BarChart2, Calendar, AlertCircle, RefreshCw, ChevronDown, CheckCircle
} from 'lucide-react';
import apiService from '../../services/api';

const SystemSettings = ({ activeTab: propActiveTab }) => {
  // Map incoming route IDs to internal tab IDs
  const tabMapping = {
    'system-settings': 'dashboard',
    'superadmin-dashboard': 'dashboard',
    'superadmin-business': 'business',
    'superadmin-subscription': 'subscription',
    'superadmin-packages': 'packages',
    'superadmin-coupons': 'coupons',
    'superadmin-settings': 'settings',
    'superadmin-communicator': 'communicator',
  };
  const getMappedTab = (tab) => tabMapping[tab] || 'dashboard';

  // Format price helper to strip legacy HTML wraps
  const formatPrice = (priceVal) => {
    if (priceVal === undefined || priceVal === null) return '$0.00';
    if (typeof priceVal === 'number') return `$${priceVal.toFixed(2)}`;

    // Strip HTML tags if present
    const cleanStr = typeof priceVal === 'string' && priceVal.includes('<')
      ? priceVal.replace(/<[^>]*>?/gm, '')
      : String(priceVal);

    const numericStr = cleanStr.replace(/[^0-9.]/g, '');
    const num = parseFloat(numericStr);
    return isNaN(num) ? '$0.00' : `$${num.toFixed(2)}`;
  };

  // Dynamic chart fallback generator
  const getChartDataWithFallback = () => {
    if (chartData && chartData.length > 0) return chartData;

    // Generate last 6 months as default $0 values
    const months = [];
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const label = `${monthNames[d.getMonth()]}-${d.getFullYear()}`;
      months.push({ month: label, value: 0 });
    }
    return months;
  };

  const [activeTab, setActiveTab] = useState(getMappedTab(propActiveTab));
  const prevPropRef = React.useRef(propActiveTab);

  useEffect(() => {
    if (propActiveTab !== prevPropRef.current) {
      setActiveTab(getMappedTab(propActiveTab));
      prevPropRef.current = propActiveTab;
    }
  }, [propActiveTab]);
  const [dateFilter, setDateFilter] = useState('this_month');

  // Dashboard Metrics & Chart
  const [metrics, setMetrics] = useState({
    newSubscriptions: 0,
    newBusinessRegistrations: 0,
    notSubscribed: 0
  });
  const [chartData, setChartData] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  // Businesses Tab State
  const [businesses, setBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [businessSearch, setBusinessSearch] = useState('');
  const [businessFilterPackage, setBusinessFilterPackage] = useState('');
  const [businessFilterStatus, setBusinessFilterStatus] = useState('');
  const [businessFilterActive, setBusinessFilterActive] = useState('');
  const [businessPage, setBusinessPage] = useState(1);
  const [businessPerPage, setBusinessPerPage] = useState(10);

  // Packages Tab State
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packagesSubView, setPackagesSubView] = useState('list'); // 'list', 'add', 'edit'

  // Subscriptions Tab State
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [subscriptionSearch, setSubscriptionSearch] = useState('');
  const [subscriptionFilterStatus, setSubscriptionFilterStatus] = useState('');
  const [subscriptionFilterPackage, setSubscriptionFilterPackage] = useState('');
  const [subscriptionPage, setSubscriptionPage] = useState(1);
  const [subscriptionPerPage, setSubscriptionPerPage] = useState(10);

  // Coupons Tab State
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponPage, setCouponPage] = useState(1);
  const [couponPerPage, setCouponPerPage] = useState(10);
  const [couponPackages, setCouponPackages] = useState([]);
  const [couponBusinesses, setCouponBusinesses] = useState([]);

  // Settings Tab State
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsInnerTab, setSettingsInnerTab] = useState('application');
  const [currenciesList, setCurrenciesList] = useState({});

  // Communicator State
  const [communicatorBusinesses, setCommunicatorBusinesses] = useState([]);
  const [communicatorHistory, setCommunicatorHistory] = useState([]);
  const [communicatorLoading, setCommunicatorLoading] = useState(false);
  const [commHistoryPage, setCommHistoryPage] = useState(1);
  const [commHistoryPerPage, setCommHistoryPerPage] = useState(10);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');

  // General UI Modals toggles
  const [activeModal, setActiveModal] = useState(null); // 'add_business', 'add_subscription', 'edit_subscription_dates', 'add_package', 'edit_package', 'add_coupon', 'edit_coupon', 'update_password', 'edit_status', 'view_business'
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalFormData, setModalFormData] = useState({});
  const [businessDetails, setBusinessDetails] = useState(null);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsTab, setDetailsTab] = useState('info');
  const [submittingPassword, setSubmittingPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState(null);

  // Pagination helper
  const PaginationControls = ({ currentPage, totalItems, perPage, onPageChange, onPerPageChange }) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
    const startItem = (currentPage - 1) * perPage + 1;
    const endItem = Math.min(currentPage * perPage, totalItems);

    const goToPage = (p) => {
      if (p >= 1 && p <= totalPages) onPageChange(p);
    };

    const pageNumbers = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (endPage - startPage < 4) {
      if (startPage === 1) endPage = Math.min(totalPages, startPage + 4);
      else if (endPage === totalPages) startPage = Math.max(1, endPage - 4);
    }
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

    return (
      <div className="sa-pagination">
        <div className="sa-pagination-info">
          Showing <strong>{totalItems === 0 ? 0 : startItem}</strong> to <strong>{endItem}</strong> of <strong>{totalItems}</strong> entries
        </div>
        <div className="sa-pagination-nav">
          <button className="sa-page-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>&laquo;</button>
          {startPage > 1 && <button className="sa-page-btn" onClick={() => goToPage(1)}>1</button>}
          {startPage > 2 && <span className="sa-page-dots">...</span>}
          {pageNumbers.map(n => (
            <button key={n} className={`sa-page-btn ${n === currentPage ? 'active' : ''}`} onClick={() => goToPage(n)}>{n}</button>
          ))}
          {endPage < totalPages - 1 && <span className="sa-page-dots">...</span>}
          {endPage < totalPages && <button className="sa-page-btn" onClick={() => goToPage(totalPages)}>{totalPages}</button>}
          <button className="sa-page-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>&raquo;</button>
        </div>
      </div>
    );
  };

  // Tab definitions
  const tabs = [
    { id: 'dashboard', label: 'Superadmin', icon: <BarChart2 size={16} /> },
    { id: 'business', label: 'All Businesses', icon: <Building size={16} /> },
    { id: 'subscription', label: 'Package Subscription', icon: <CreditCard size={16} /> },
    { id: 'packages', label: 'Packages', icon: <PackageIcon size={16} /> },
    { id: 'coupons', label: 'Coupons', icon: <Ticket size={16} /> },
    { id: 'settings', label: 'Super Admin Settings', icon: <SettingsIcon size={16} /> },
    { id: 'communicator', label: 'Communicator', icon: <Mail size={16} /> }
  ];

  // Helper date range formatters
  const getDateRange = (filterType) => {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];

    switch (filterType) {
      case 'today':
        return { start: formatDate(today), end: formatDate(today) };
      case 'this_week':
        const dayOfWeek = today.getDay(); // 0 is Sunday, 1 is Monday
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + mondayOffset);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return { start: formatDate(monday), end: formatDate(sunday) };
      case 'this_month':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { start: formatDate(firstDay), end: formatDate(lastDay) };
      case 'this_year':
        const startYear = new Date(today.getFullYear(), 0, 1);
        const endYear = new Date(today.getFullYear(), 11, 31);
        return { start: formatDate(startYear), end: formatDate(endYear) };
      default:
        return { start: formatDate(today), end: formatDate(today) };
    }
  };

  // Fetch Dashboard Stats & Chart
  const fetchDashboardData = async () => {
    setLoadingDashboard(true);
    try {
      const dates = getDateRange(dateFilter);
      const statsRes = await apiService.getSuperadminStats(dates.start, dates.end);
      if (statsRes) {
        setMetrics({
          newSubscriptions: statsRes.new_subscriptions || 0,
          newBusinessRegistrations: statsRes.new_registrations || 0,
          notSubscribed: statsRes.not_subscribed || 0
        });
      }

      const chartRes = await apiService.getSuperadminMonthlyChart();
      if (chartRes) {
        // Convert monthly object to array
        const formattedChart = Object.entries(chartRes).map(([month, val]) => ({
          month,
          value: parseFloat(val) || 0
        }));
        setChartData(formattedChart);
      }
    } catch (err) {
      /* console.error('Failed to load dashboard data', err); */
    } finally {
      setLoadingDashboard(false);
    }
  };

  // Fetch Business Create Data (Currencies, etc.)
  const fetchBusinessCreateData = async () => {
    try {
      const res = await apiService.getSuperadminBusinessCreateData();
      if (res && res.currencies) {
        setCurrenciesList(res.currencies);
      }
    } catch (err) {
      try {
        const settingsRes = await apiService.getBusinessSettings();
        if (settingsRes && settingsRes.data && settingsRes.data.currencies) {
          setCurrenciesList(settingsRes.data.currencies);
        }
      } catch (e) {}
    }
  };

  // Fetch Businesses
  const fetchBusinesses = async () => {
    setLoadingBusinesses(true);
    try {
      const res = await apiService.getSuperadminBusinesses();
      if (res && res.data) {
        setBusinesses(res.data);
      }
    } catch (err) {
      /* console.error('Failed to load businesses', err); */
    } finally {
      setLoadingBusinesses(false);
    }
  };

  // Fetch Subscriptions
  const fetchSubscriptions = async () => {
    setLoadingSubscriptions(true);
    try {
      const res = await apiService.getSuperadminSubscriptionsList();
      if (res && res.data) {
        setSubscriptions(res.data);
      }
    } catch (err) {
      /* console.error('Failed to load subscriptions', err); */
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  // Fetch Packages
  const fetchPackages = async () => {
    setLoadingPackages(true);
    try {
      const res = await apiService.getSuperadminPackages();
      if (res) {
        if (res.packages && res.packages.data) {
          setPackages(res.packages.data);
        } else if (res.data) {
          setPackages(res.data);
        } else if (Array.isArray(res)) {
          setPackages(res);
        } else {
          setPackages([]);
        }
      }
    } catch (err) {
      /* console.error('Failed to load packages', err); */
    } finally {
      setLoadingPackages(false);
    }
  };

  // Fetch Coupons
  const fetchCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const res = await apiService.getCoupons();
      if (res) {
        if (Array.isArray(res.data)) {
          setCoupons(res.data);
        } else if (Array.isArray(res)) {
          setCoupons(res);
        } else {
          setCoupons([]);
        }
      }
    } catch (err) {
      /* console.error('Failed to load coupons', err); */
    } finally {
      setLoadingCoupons(false);
    }
  };

  // Fetch Coupon Create Data (packages + businesses)
  const fetchCouponCreateData = async () => {
    try {
      const res = await apiService.getCouponCreateData();
      if (res) {
        if (res.packages) {
          setCouponPackages(Object.entries(res.packages).map(([id, name]) => ({ id: Number(id), name })));
        }
        if (res.businesses) {
          setCouponBusinesses(Object.entries(res.businesses).map(([id, name]) => ({ id: Number(id), name })));
        }
      }
    } catch (err) {
      /* console.error('Failed to load coupon create data', err); */
    }
  };

  // Fetch Settings
  const fetchSettings = async () => {
    setLoadingSettings(true);
    try {
      const res = await apiService.getSuperadminSettings();
      if (res) {
        setSettings(res);
      }
    } catch (err) {
      /* console.error('Failed to load settings', err); */
    } finally {
      setLoadingSettings(false);
    }
  };

  // Fetch Communicator Data
  const fetchCommunicatorData = async () => {
    setCommunicatorLoading(true);
    try {
      const bizRes = await apiService.getCommunicatorData();
      if (bizRes && bizRes.businesses) {
        setCommunicatorBusinesses(Object.entries(bizRes.businesses).map(([id, name]) => ({ id, name })));
      }
      const historyRes = await apiService.getCommunicatorHistory();
      if (historyRes && historyRes.data) {
        setCommunicatorHistory(historyRes.data);
      }
    } catch (err) {
      /* console.error('Failed to load communicator data', err); */
    } finally {
      setCommunicatorLoading(false);
    }
  };

  // Trigger loads based on active tab
  useEffect(() => {
    setPackagesSubView('list');
    if (activeTab === 'dashboard') {
      fetchDashboardData();
    } else if (activeTab === 'business') {
      fetchBusinesses();
      fetchPackages();
      fetchBusinessCreateData();
    } else if (activeTab === 'subscription') {
      fetchSubscriptions();
      fetchPackages();
    } else if (activeTab === 'packages') {
      fetchPackages();
    } else if (activeTab === 'coupons') {
      fetchCoupons();
      fetchPackages();
      fetchBusinesses();
    } else if (activeTab === 'settings') {
      fetchSettings();
    } else if (activeTab === 'communicator') {
      fetchCommunicatorData();
    }
  }, [activeTab, dateFilter]);

  // Handle Notifications/Alert messages
  const showFeedback = (msg, success = true) => {
    setStatusMessage({ msg, type: success ? 'success' : 'error' });
    setTimeout(() => setStatusMessage(null), 5000);
  };

  // Delete Handlers
  const handleDeleteBusiness = async (id) => {
    if (window.confirm("Are you sure you want to delete this business? This will delete all products, invoices, and transactions.")) {
      try {
        const res = await apiService.deleteBusiness(id);
        if (res.success) {
          showFeedback("Business deleted successfully.");
          fetchBusinesses();
        } else {
          showFeedback(res.msg || "Failed to delete business.", false);
        }
      } catch (err) {
        showFeedback("Something went wrong.", false);
      }
    }
  };

  const handleDeletePackage = async (id) => {
    if (window.confirm("Are you sure you want to delete this package?")) {
      try {
        const res = await apiService.deletePackage(id);
        if (res.success) {
          showFeedback("Package deleted successfully.");
          fetchPackages();
        } else {
          showFeedback(res.msg || "Failed to delete package.", false);
        }
      } catch (err) {
        showFeedback("Something went wrong.", false);
      }
    }
  };

  const handleDeleteCoupon = async (id) => {
    if (window.confirm("Are you sure you want to delete this coupon?")) {
      try {
        const res = await apiService.deleteCoupon(id);
        if (res.success) {
          showFeedback("Coupon deleted successfully.");
          fetchCoupons();
        } else {
          showFeedback(res.msg || "Failed to delete coupon.", false);
        }
      } catch (err) {
        showFeedback("Something went wrong.", false);
      }
    }
  };

  // Toggle Business Active
  const isActive = (val) => {
    if (val == 1 || val === true || val === '1') return true;
    if (typeof val === 'string') {
      const lower = val.toLowerCase();
      return lower.includes('bg-green') || lower.includes('label-success') || lower.includes('>active<');
    }
    return false;
  };

  const handleToggleBusinessActive = async (id, currentStatus) => {
    try {
      const nextStatus = isActive(currentStatus) ? 0 : 1;
      const res = await apiService.toggleBusinessActive(id, nextStatus);
      if (res.success) {
        showFeedback("Business status updated successfully.");
        fetchBusinesses();
      } else {
        showFeedback("Failed to update status.", false);
      }
    } catch (err) {
      showFeedback("Something went wrong.", false);
    }
  };

  // Manage Business Details Click
  const handleManageBusinessClick = async (biz) => {
    setSelectedItem(biz);
    setBusinessDetails(null);
    setBusinessUsers([]);
    setDetailsTab('info');
    setActiveModal('view_business');
    setLoadingDetails(true);
    try {
      const detailsRes = await apiService.getSuperadminBusinessDetails(biz.id);
      if (detailsRes) {
        setBusinessDetails(detailsRes.business || detailsRes);
      }
      const usersRes = await apiService.getSuperadminBusinessUsers(biz.id);
      setBusinessUsers(usersRes.data || usersRes || []);
    } catch (err) {
      /* console.error("Error loading business manage details:", err); */
      toast.error("Failed to load business details");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Login as User Impersonation
  const handleLoginAsUser = async (user) => {
    if (!window.confirm(`Are you sure you want to log in as ${user.username}?`)) return;
    try {
      const superadminToken = localStorage.getItem('etpos_token');
      const superadminUser = localStorage.getItem('etpos_user');
      if (superadminToken) {
        localStorage.setItem('etpos_superadmin_backup_token', superadminToken);
      }
      if (superadminUser) {
        localStorage.setItem('etpos_superadmin_backup_user', superadminUser);
      }

      const res = await apiService.loginAsUser(user.id);
      if (res.success && (res.access_token || res.token)) {
        const newToken = res.access_token || res.token;

        // Clear all cached settings from previous business to guarantee 100% clean isolation
        apiService.clearCachedBusinessSettings();

        // Immediately update business name & logo for splash loader
        const targetBiz = res.user?.business || {};
        const targetName = targetBiz.name || res.user?.business_name || 'Business';
        const targetLogo = targetBiz.logo || res.user?.business_logo || null;

        if (targetName) localStorage.setItem('etpos_business_name', targetName);
        if (targetLogo) localStorage.setItem('etpos_business_logo', targetLogo);

        const expiryTime = new Date();
        expiryTime.setHours(expiryTime.getHours() + 24);

        // Store new token and auth state immediately so context switch persists across reloads
        localStorage.setItem('etpos_token', newToken);
        localStorage.setItem('etpos_token_expiry', expiryTime.toISOString());
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('etpos_user', JSON.stringify(res.user));
        localStorage.setItem('user', JSON.stringify({
          username: user.username,
          loginTime: new Date().toISOString(),
          ...res.user
        }));

        // Fetch fresh business settings with the NEW token (impersonated user's business)
        try {
          const settingsRes = await fetch(`${window.location.origin}/connector/api/user/loggedin`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${newToken}`,
              'Content-Type': 'application/json'
            }
          });
          if (settingsRes.ok) {
            const settingsData = await settingsRes.json();
            const biz = settingsData.data?.business || settingsData.data?.user?.business || settingsData.business;
            if (biz) {
              apiService.cacheBusinessSettingsFromData(biz);
            }
          }
        } catch (settingsErr) {
          const symbol = res.user?.currency_symbol || res.user?.business?.currency?.symbol || res.user?.business?.currency_symbol;
          if (symbol && symbol !== '$') localStorage.setItem('etpos_currency_symbol', symbol);
          const tz = res.user?.time_zone || res.user?.business?.time_zone;
          if (tz) localStorage.setItem('etpos_business_timezone', tz);
        }

        toast.success(`Context switched successfully to ${user.username}`);
        window.location.href = '/';
      } else {
        toast.error(res.message || res.msg || 'Failed to switch context');
      }
    } catch (err) {
      /* console.error('Context switch error:', err); */
      toast.error('Failed to switch context: ' + err.message);
    }
  };

  // Update Password Submit
  const handleUpdatePasswordSubmit = async (e) => {
    e.preventDefault();
    const { password, confirm_password } = modalFormData;
    if (password !== confirm_password) {
      setFormErrors({ confirm_password: "Passwords do not match." });
      return;
    }
    setSubmittingPassword(true);
    try {
      const userId = modalFormData.user_id || selectedItem.id;
      const res = await apiService.updateSuperadminUserPassword({
        user_id: userId,
        password: password
      });
      if (res.success || res.status) {
        toast.success("Password updated successfully.");
        setActiveModal(null);
        if (selectedItem?.business_id) {
          const usersRes = await apiService.getSuperadminBusinessUsers(selectedItem.business_id);
          setBusinessUsers(usersRes.data || usersRes || []);
        }
      } else {
        toast.error(res.msg || "Failed to update password.");
      }
    } catch (err) {
      toast.error("Failed to update password: " + err.message);
    } finally {
      setSubmittingPassword(false);
    }
  };

  // Add Business Submit
  const handleAddBusinessSubmit = async (e) => {
    e.preventDefault();
    if (modalFormData.password !== modalFormData.confirm_password) {
      showFeedback("Password and confirm password do not match.", false);
      return;
    }
    try {
      const res = await apiService.addBusiness(modalFormData);
      if (res.success || res.status) {
        showFeedback("Business created successfully.");
        setActiveModal(null);
        fetchBusinesses();
      } else {
        showFeedback(res.msg || "Failed to create business.", false);
      }
    } catch (err) {
      showFeedback("Something went wrong.", false);
    }
  };

  // Add Package Submit
  const handleAddPackageSubmit = async (e) => {
    e.preventDefault();
    try {
      let res;
      const payload = {
        ...modalFormData,
        is_active: modalFormData.is_active ? 1 : 0,
        mark_package_as_popular: modalFormData.mark_package_as_popular ? 1 : 0,
        is_private: modalFormData.is_private ? 1 : 0,
        is_one_time: modalFormData.is_one_time ? 1 : 0,
        enable_custom_link: modalFormData.enable_custom_link ? 1 : 0,
        businesses: modalFormData.businesses && modalFormData.businesses.length > 0 ? modalFormData.businesses : null
      };

      if (selectedItem) {
        res = await apiService.updatePackage(selectedItem.id, payload);
      } else {
        res = await apiService.addPackage(payload);
      }
      if (res.success || res.status || res.success === undefined) {
        // Handle variations of return data from controller
        showFeedback(`Package ${selectedItem ? 'updated' : 'created'} successfully.`);
        setPackagesSubView('list');
        setActiveModal(null);
        fetchPackages();
      } else {
        showFeedback(res.msg || "Failed to process package.", false);
      }
    } catch (err) {
      /* console.error("Package save error", err); */
      showFeedback("Something went wrong.", false);
    }
  };

  // Add Coupon Submit
  const handleAddCouponSubmit = async (e) => {
    e.preventDefault();
    try {
      let res;
      if (selectedItem) {
        res = await apiService.updateCoupon(selectedItem.id, modalFormData);
      } else {
        res = await apiService.addCoupon(modalFormData);
      }
      if (res.success) {
        showFeedback(`Coupon ${selectedItem ? 'updated' : 'created'} successfully.`);
        setActiveModal(null);
        fetchCoupons();
      } else {
        showFeedback(res.msg || "Failed to process coupon.", false);
      }
    } catch (err) {
      showFeedback("Something went wrong.", false);
    }
  };

  // Add Subscription Submit
  const handleAddSubscriptionSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await apiService.addSuperadminSubscription(modalFormData);
      if (res.success) {
        showFeedback("Subscription added successfully.");
        setActiveModal(null);
        if (activeTab === 'business') fetchBusinesses();
        else fetchSubscriptions();
      } else {
        showFeedback(res.msg || "Failed to add subscription.", false);
      }
    } catch (err) {
      showFeedback("Something went wrong.", false);
    }
  };

  // Edit Subscription Status Submit
  const handleEditStatusSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await apiService.updateSuperadminSubscriptionStatus(selectedItem.id, modalFormData);
      if (res.success) {
        showFeedback("Subscription status updated successfully.");
        setActiveModal(null);
        fetchSubscriptions();
      } else {
        showFeedback(res.msg || "Failed to update status.", false);
      }
    } catch (err) {
      showFeedback("Something went wrong.", false);
    }
  };

  // Edit Subscription Dates Submit
  const handleEditDatesSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await apiService.updateSuperadminSubscriptionDates({
        subscription_id: selectedItem.id,
        ...modalFormData
      });
      if (res.success) {
        showFeedback("Subscription dates updated successfully.");
        setActiveModal(null);
        fetchSubscriptions();
      } else {
        showFeedback(res.msg || "Failed to update dates.", false);
      }
    } catch (err) {
      showFeedback("Something went wrong.", false);
    }
  };

  // Save Settings Submit
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      const s = settings;
      const dv = s.default_values || {};
      const payload = {
        // System settings (DB stored)
        app_currency_id: s.settings?.app_currency_id || '',
        invoice_business_name: s.settings?.invoice_business_name || '',
        email: s.settings?.email || '',
        invoice_business_landmark: s.settings?.invoice_business_landmark || '',
        invoice_business_zip: s.settings?.invoice_business_zip || '',
        invoice_business_state: s.settings?.invoice_business_state || '',
        invoice_business_city: s.settings?.invoice_business_city || '',
        invoice_business_country: s.settings?.invoice_business_country || '',
        package_expiry_alert_days: s.settings?.package_expiry_alert_days || '',
        superadmin_register_tc: s.settings?.superadmin_register_tc || '',
        welcome_email_subject: s.settings?.welcome_email_subject || '',
        welcome_email_body: s.settings?.welcome_email_body || '',
        additional_js: s.settings?.additional_js || '',
        additional_css: s.settings?.additional_css || '',
        offline_payment_details: s.settings?.offline_payment_details || '',
        // Checkboxes (DB stored)
        enable_business_based_username: s.settings?.enable_business_based_username || 0,
        superadmin_enable_register_tc: s.settings?.superadmin_enable_register_tc || 0,
        allow_email_settings_to_businesses: s.settings?.allow_email_settings_to_businesses || 0,
        enable_new_business_registration_notification: s.settings?.enable_new_business_registration_notification || 0,
        enable_new_subscription_notification: s.settings?.enable_new_subscription_notification || 0,
        enable_welcome_email: s.settings?.enable_welcome_email || 0,
        enable_offline_payment: s.settings?.enable_offline_payment || 0,
        // ENV settings
        APP_NAME: dv.APP_NAME || '',
        APP_TITLE: dv.APP_TITLE || '',
        APP_LOCALE: dv.APP_LOCALE || '',
        MAIL_MAILER: dv.MAIL_MAILER || '',
        MAIL_HOST: dv.MAIL_HOST || '',
        MAIL_PORT: dv.MAIL_PORT || '',
        MAIL_USERNAME: dv.MAIL_USERNAME || '',
        MAIL_PASSWORD: dv.MAIL_PASSWORD || '',
        MAIL_ENCRYPTION: dv.MAIL_ENCRYPTION || '',
        MAIL_FROM_ADDRESS: dv.MAIL_FROM_ADDRESS || '',
        MAIL_FROM_NAME: dv.MAIL_FROM_NAME || '',
        STRIPE_PUB_KEY: dv.STRIPE_PUB_KEY || '',
        STRIPE_SECRET_KEY: dv.STRIPE_SECRET_KEY || '',
        PAYPAL_MODE: dv.PAYPAL_MODE || '',
        PAYPAL_CLIENT_ID: dv.PAYPAL_CLIENT_ID || '',
        PAYPAL_APP_SECRET: dv.PAYPAL_APP_SECRET || '',
        BACKUP_DISK: dv.BACKUP_DISK || '',
        DROPBOX_ACCESS_TOKEN: dv.DROPBOX_ACCESS_TOKEN || '',
        RAZORPAY_KEY_ID: dv.RAZORPAY_KEY_ID || '',
        RAZORPAY_KEY_SECRET: dv.RAZORPAY_KEY_SECRET || '',
        PESAPAL_CONSUMER_KEY: dv.PESAPAL_CONSUMER_KEY || '',
        PESAPAL_CONSUMER_SECRET: dv.PESAPAL_CONSUMER_SECRET || '',
        PESAPAL_LIVE: dv.PESAPAL_LIVE || 'false',
        PUSHER_APP_ID: dv.PUSHER_APP_ID || '',
        PUSHER_APP_KEY: dv.PUSHER_APP_KEY || '',
        PUSHER_APP_SECRET: dv.PUSHER_APP_SECRET || '',
        PUSHER_APP_CLUSTER: dv.PUSHER_APP_CLUSTER || '',
        GOOGLE_MAP_API_KEY: dv.GOOGLE_MAP_API_KEY || '',
        PAYSTACK_PUBLIC_KEY: dv.PAYSTACK_PUBLIC_KEY || '',
        PAYSTACK_SECRET_KEY: dv.PAYSTACK_SECRET_KEY || '',
        FLUTTERWAVE_PUBLIC_KEY: dv.FLUTTERWAVE_PUBLIC_KEY || '',
        FLUTTERWAVE_SECRET_KEY: dv.FLUTTERWAVE_SECRET_KEY || '',
        FLUTTERWAVE_ENCRYPTION_KEY: dv.FLUTTERWAVE_ENCRYPTION_KEY || '',
        MY_FATOORAH_API_KEY: dv.MY_FATOORAH_API_KEY || '',
        MY_FATOORAH_COUNTRY_ISO: dv.MY_FATOORAH_COUNTRY_ISO || '',
        MY_FATOORAH_IS_TEST: dv.MY_FATOORAH_IS_TEST || 'false',
        ALLOW_REGISTRATION: dv.ALLOW_REGISTRATION || '',
      };
      const res = await apiService.updateSuperadminSettings(payload);
      if (res.success) {
        showFeedback("Settings saved successfully.");
        fetchSettings();
      } else {
        showFeedback(res.msg || "Failed to save settings.", false);
      }
    } catch (err) {
      showFeedback("Something went wrong.", false);
    }
  };

  // Send MASS message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (selectedRecipients.length === 0) {
      alert("Please select at least one recipient.");
      return;
    }
    try {
      const res = await apiService.sendCommunicatorNotification({
        recipients: selectedRecipients,
        subject: messageSubject,
        message: messageBody
      });
      if (res.success) {
        showFeedback("Mass notification sent successfully!");
        setMessageSubject('');
        setMessageBody('');
        setSelectedRecipients([]);
        fetchCommunicatorData();
      } else {
        showFeedback(res.msg || "Failed to send communicator notification.", false);
      }
    } catch (err) {
      showFeedback("Something went wrong.", false);
    }
  };

  // Helper filters
  const filteredBusinesses = businesses.filter(biz => {
    const nameMatches = (biz.name || '').toLowerCase().includes(businessSearch.toLowerCase()) ||
      (biz.owner_name || '').toLowerCase().includes(businessSearch.toLowerCase()) ||
      (biz.owner_email || '').toLowerCase().includes(businessSearch.toLowerCase());
    const packageMatches = businessFilterPackage === '' || biz.package_name === businessFilterPackage;
    const activeMatches = businessFilterActive === '' ||
      (businessFilterActive === 'active' && biz.is_active == 1) ||
      (businessFilterActive === 'inactive' && biz.is_active == 0);
    return nameMatches && packageMatches && activeMatches;
  });

  const filteredSubscriptions = subscriptions.filter(sub => {
    const searchMatches = (sub.business_name || '').toLowerCase().includes(subscriptionSearch.toLowerCase()) ||
      (sub.package_name || '').toLowerCase().includes(subscriptionSearch.toLowerCase()) ||
      (sub.payment_transaction_id || '').toLowerCase().includes(subscriptionSearch.toLowerCase());
    const statusMatches = subscriptionFilterStatus === '' || sub.status === subscriptionFilterStatus;
    const packageMatches = subscriptionFilterPackage === '' || sub.package_name === subscriptionFilterPackage;
    return searchMatches && statusMatches && packageMatches;
  });

  // Paginated slices
  const paginatedBusinesses = filteredBusinesses.slice((businessPage - 1) * businessPerPage, businessPage * businessPerPage);
  const paginatedSubscriptions = filteredSubscriptions.slice((subscriptionPage - 1) * subscriptionPerPage, subscriptionPage * subscriptionPerPage);
  const paginatedCoupons = coupons.slice((couponPage - 1) * couponPerPage, couponPage * couponPerPage);
  const paginatedCommHistory = communicatorHistory.slice((commHistoryPage - 1) * commHistoryPerPage, commHistoryPage * commHistoryPerPage);

  // Safe html stripper helper for DataTables pre-rendered components
  const cleanHtmlString = (str) => {
    if (typeof str !== 'string') return str;
    const txt = document.createElement("textarea");
    txt.innerHTML = str;
    return txt.value.replace(/<[^>]*>?/gm, '');
  };

  return (
    <div className="superadmin-dashboard">
      {statusMessage && (
        <div className={`status-alert ${statusMessage.type}`}>
          <AlertCircle size={20} />
          <span>{statusMessage.msg}</span>
        </div>
      )}

      {/* Header */}
      <div className="dashboard-header sa-action-bar">
        <h2>Welcome Superadmin</h2>
        {activeTab === 'dashboard' && (
          <div className="date-filter-group">
            {['today', 'this_week', 'this_month', 'this_year'].map(filter => (
              <button
                key={filter}
                className={`filter-btn ${dateFilter === filter ? 'active' : ''}`}
                onClick={() => setDateFilter(filter)}
              >
                {filter.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-nav">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="tabs-content">
        {/* ========================================================
            TAB: DASHBOARD
            ======================================================== */}
        {activeTab === 'dashboard' && (
          <div className="tab-content">
            {loadingDashboard ? (
              <>
                <div className="metric-cards-container">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="skeleton-card">
                      <div className="skeleton-icon"></div>
                      <div className="skeleton-content">
                        <div className="skeleton-line short"></div>
                        <div className="skeleton-line medium"></div>
                        <div className="skeleton-line long"></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="loading-state" style={{ minHeight: '120px', marginTop: '1rem' }}>
                  <RefreshCw className="animate-spin" /> Loading stats...
                </div>
              </>
            ) : (
              <>
                <div className="metric-cards-container">
                  <div className="metric-card blue">
                    <div className="metric-icon">
                      <RefreshCw size={28} />
                    </div>
                    <div className="metric-content">
                      <div className="metric-value">$ {metrics.newSubscriptions.toFixed(2)}</div>
                      <div className="metric-label">New Subscriptions</div>
                      <span className="metric-link cursor-pointer" onClick={() => setActiveTab('subscription')}>More Information →</span>
                    </div>
                  </div>

                  <div className="metric-card light-blue">
                    <div className="metric-icon">
                      <UserPlus size={28} />
                    </div>
                    <div className="metric-content">
                      <div className="metric-value">{metrics.newBusinessRegistrations}</div>
                      <div className="metric-label">New Business Registrations</div>
                      <span className="metric-link cursor-pointer" onClick={() => setActiveTab('business')}>More Information →</span>
                    </div>
                  </div>

                  <div className="metric-card pink">
                    <div className="metric-icon">
                      <PieChart size={28} />
                    </div>
                    <div className="metric-content">
                      <div className="metric-value">{metrics.notSubscribed}</div>
                      <div className="metric-label">Not Subscribed</div>
                      <span className="metric-link cursor-pointer" onClick={() => setActiveTab('business')}>More Information →</span>
                    </div>
                  </div>
                </div>

                {/* Sales Chart */}
                <div className="chart-section">
                  <div className="chart-header">
                    <h3>
                      <BarChart2 size={20} style={{ marginRight: '8px', color: '#5B9BD5' }} />
                      Sale - Monthly for 12 months
                    </h3>
                  </div>

                  <div className="custom-chart-container">
                    <div className="chart-y-axis">
                      <div className="y-label">10k</div>
                      <div className="y-label">5k</div>
                      <div className="y-label">2k</div>
                      <div className="y-label">0</div>
                    </div>
                    <div className="chart-bars-container">
                      {getChartDataWithFallback().map((data, index) => {
                        const finalChart = getChartDataWithFallback();
                        const maxVal = Math.max(...finalChart.map(d => d.value), 1);
                        const pct = data.value === 0 ? 3 : Math.min((data.value / maxVal) * 100, 100);
                        return (
                          <div key={index} className="bar-wrapper">
                            <div className="bar-column">
                              <div
                                className="bar"
                                style={{ height: `${pct}%` }}
                                title={`${data.month}: $${data.value.toLocaleString()}`}
                              ></div>
                            </div>
                            <div className="bar-label">{data.month}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="chart-legend">
                    <span className="legend-dot"></span>
                    <span className="legend-text">Total Subscriptions (Dollars)</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ========================================================
            TAB: ALL BUSINESSES
            ======================================================== */}
        {activeTab === 'business' && (
          <div className="tab-content">
            <div className="sa-action-bar">
              <h3>Business Management</h3>
              <button className="add-btn" onClick={() => {
                setModalFormData({
                  currency_id: '',
                  time_zone: 'Asia/Kolkata',
                  country: '',
                  state: '',
                  city: '',
                  zip_code: '',
                  landmark: '',
                  mobile: '',
                  alternate_number: '',
                  website: ''
                });
                setActiveModal('add_business');
              }}>
                <Plus size={16} /> Add New Business
              </button>
            </div>

            {/* Filters Row */}
            <div className="filters-row">
              <div className="search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search businesses..."
                  value={businessSearch}
                  onChange={(e) => setBusinessSearch(e.target.value)}
                />
              </div>

              <select
                value={businessFilterPackage}
                onChange={(e) => setBusinessFilterPackage(e.target.value)}
                className="sa-form-select"
                style={{ width: 'auto', minWidth: '140px' }}
              >
                <option value="">All Packages</option>
                {packages.map(pkg => (
                  <option key={pkg.id} value={pkg.name}>{pkg.name}</option>
                ))}
              </select>

              <select
                value={businessFilterActive}
                onChange={(e) => setBusinessFilterActive(e.target.value)}
                className="sa-form-select"
                style={{ width: 'auto', minWidth: '140px' }}
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <select
                className="sa-per-page-select"
                value={businessPerPage}
                onChange={(e) => { setBusinessPerPage(Number(e.target.value)); setBusinessPage(1); }}
              >
                <option value={10}>10 rows</option>
                <option value={25}>25 rows</option>
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
              </select>
            </div>

            {loadingBusinesses ? (
              <div className="loading-state"><RefreshCw className="animate-spin" /> Loading businesses...</div>
            ) : (
              <div className="table-responsive">
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Business Name</th>
                      <th>Owner</th>
                      <th>Contact</th>
                      <th>Subscription</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBusinesses.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="sa-text-center sa-text-muted" style={{ padding: '2rem' }}>No matching businesses found.</td>
                      </tr>
                    ) : (
                      paginatedBusinesses.map((biz) => (
                        <tr key={biz.id}>
                          <td>
                            <strong className="sa-text-blue" style={{ cursor: 'pointer' }} onClick={() => handleManageBusinessClick(biz)}>
                              {cleanHtmlString(biz.name)}
                            </strong>
                            <div className="sa-text-muted-sm">Created by: {biz.biz_creator || 'Self'}</div>
                          </td>
                          <td>
                            <div>{biz.owner_name}</div>
                            <div className="sa-text-muted-sm">{biz.owner_email}</div>
                          </td>
                          <td>{biz.contact_number || biz.mobile || 'N/A'}</td>
                          <td>
                            {biz.package_name ? (
                              <span className="sa-badge-blue">
                                {biz.package_name}
                              </span>
                            ) : (
                              <span className="sa-text-muted-sm">No active plan</span>
                            )}
                            {biz.start_date && (
                              <div className="sa-text-muted-sm" style={{ marginTop: '0.25rem' }}>{biz.start_date} to {biz.end_date}</div>
                            )}
                          </td>
                          <td>
                            <span
                              className={`badge-btn ${isActive(biz.is_active) ? 'status-badge active' : 'status-badge inactive'}`}
                            >
                              {isActive(biz.is_active) ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="sa-action-stack">
                              <button
                                className="sa-action-btn primary"
                                onClick={() => handleManageBusinessClick(biz)}
                              >
                                Manage
                              </button>
                              <button
                                className="sa-action-btn primary"
                                onClick={() => {
                                  setModalFormData({
                                    business_id: biz.id,
                                    paid_via: 'offline'
                                  });
                                  setSelectedItem(biz);
                                  setActiveModal('add_subscription');
                                }}
                              >
                                Add Subscription
                              </button>
                              <button
                                className={`sa-action-btn ${isActive(biz.is_active) ? 'danger' : 'success'}`}
                                onClick={() => handleToggleBusinessActive(biz.id, biz.is_active)}
                              >
                                {isActive(biz.is_active) ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                className="sa-action-btn danger"
                                onClick={() => handleDeleteBusiness(biz.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <PaginationControls
              currentPage={businessPage}
              totalItems={filteredBusinesses.length}
              perPage={businessPerPage}
              onPageChange={setBusinessPage}
            />
          </div>
        )}

        {/* ========================================================
            TAB: PACKAGE SUBSCRIPTIONS
            ======================================================== */}
        {activeTab === 'subscription' && (
          <div className="tab-content">
            <div className="sa-action-bar" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Package Subscription <small style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: 500 }}>View Subscription</small></h3>
              <button className="add-btn" onClick={() => {
                setModalFormData({ paid_via: 'offline' });
                setActiveModal('add_subscription');
              }}>
                <Plus size={16} /> Add Subscription
              </button>
            </div>

            {/* Filters */}
            <div className="filters-row">
              <div className="search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search subscriptions..."
                  value={subscriptionSearch}
                  onChange={(e) => setSubscriptionSearch(e.target.value)}
                />
              </div>

              <select
                value={subscriptionFilterPackage}
                onChange={(e) => setSubscriptionFilterPackage(e.target.value)}
                className="sa-form-select"
                style={{ width: 'auto', minWidth: '160px' }}
              >
                <option value="">All Packages</option>
                {packages.map(pkg => (
                  <option key={pkg.id} value={pkg.name}>{pkg.name}</option>
                ))}
              </select>

              <select
                value={subscriptionFilterStatus}
                onChange={(e) => setSubscriptionFilterStatus(e.target.value)}
                className="sa-form-select"
                style={{ width: 'auto', minWidth: '140px' }}
              >
                <option value="">All Statuses</option>
                <option value="approved">Approved</option>
                <option value="waiting">Waiting</option>
                <option value="declined">Declined</option>
              </select>

              <select
                className="sa-per-page-select"
                value={subscriptionPerPage}
                onChange={(e) => { setSubscriptionPerPage(Number(e.target.value)); setSubscriptionPage(1); }}
              >
                <option value={10}>10 rows</option>
                <option value={25}>25 rows</option>
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
              </select>
            </div>

            {loadingSubscriptions ? (
              <div className="loading-state"><RefreshCw className="animate-spin" /> Loading subscriptions...</div>
            ) : (
              <div className="table-responsive">
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Business Name</th>
                      <th>Package Name</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Start Date</th>
                      <th>Trial End Date</th>
                      <th>End Date</th>
                      <th>Coupon Code</th>
                      <th>Original Price</th>
                      <th>Paid Amount</th>
                      <th>Paid Via</th>
                      <th>Transaction Id</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSubscriptions.length === 0 ? (
                      <tr>
                        <td colSpan="13" className="sa-text-center sa-text-muted" style={{ padding: '2rem' }}>No matching subscriptions found.</td>
                      </tr>
                    ) : (
                      paginatedSubscriptions.map((sub) => (
                        <tr key={sub.id}>
                          <td><strong>{sub.business_name}</strong></td>
                          <td>{sub.package_name}</td>
                          <td>
                            <span className={`status-badge ${sub.status}`}>
                              {sub.status === 'approved' ? 'Approved' :
                                sub.status === 'waiting' ? 'Waiting' :
                                  sub.status === 'declined' ? 'Declined' : sub.status}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{sub.created_at || '—'}</td>
                          <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{sub.start_date_display || '—'}</td>
                          <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{sub.trial_end_date_display || '—'}</td>
                          <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{sub.end_date_display || '—'}</td>
                          <td>{sub.coupon_code || '—'}</td>
                          <td>{formatPrice(sub.original_price)}</td>
                          <td>{formatPrice(sub.package_price)}</td>
                          <td><span style={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600 }}>{sub.paid_via || '—'}</span></td>
                          <td><code style={{ fontSize: '0.75rem' }}>{sub.payment_transaction_id || '—'}</code></td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.375rem' }}>
                              <button
                                className="sa-action-btn primary"
                                onClick={() => {
                                  setSelectedItem(sub);
                                  setModalFormData({ status: sub.status, payment_transaction_id: sub.payment_transaction_id || '' });
                                  setActiveModal('edit_status');
                                }}
                              >
                                Status
                              </button>
                              <button
                                className="sa-action-btn"
                                onClick={() => {
                                  setSelectedItem(sub);
                                  setModalFormData({
                                    start_date: sub.start_date || '',
                                    end_date: sub.end_date || '',
                                    trial_end_date: sub.trial_end_date || ''
                                  });
                                  setActiveModal('edit_subscription_dates');
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <PaginationControls
              currentPage={subscriptionPage}
              totalItems={filteredSubscriptions.length}
              perPage={subscriptionPerPage}
              onPageChange={setSubscriptionPage}
            />
          </div>
        )}

        {/* ========================================================
            TAB: PACKAGES (SaaS Plans)
            ======================================================== */}
        {activeTab === 'packages' && (
          <div className="tab-content">
            <div className="pkg-tab-header">
              <h3 className="pkg-tab-title">
                Packages <small>
                  {packagesSubView === 'list' ? 'All Packages' : selectedItem ? 'Edit package' : 'Add New package'}
                </small>
              </h3>
              {packagesSubView === 'list' && (
                <button
                  className="pkg-add-btn"
                  onClick={() => {
                    setSelectedItem(null);
                    setModalFormData({
                      is_active: 1,
                      interval: 'months',
                      interval_count: 1,
                      price: '0.00',
                      custom_permissions: {}
                    });
                    setPackagesSubView('add');
                  }}
                >
                  <Plus size={14} /> Add
                </button>
              )}
            </div>

            {loadingPackages ? (
              <div className="loading-state"><RefreshCw className="animate-spin" /> Loading packages...</div>
            ) : packagesSubView === 'list' ? (
              <div className="pkg-grid">
                {packages.length === 0 ? (
                  <div className="pkg-empty">No packages created yet.</div>
                ) : (
                  packages.map((pkg) => {
                    const perms = pkg.custom_permissions ? (typeof pkg.custom_permissions === 'string' ? JSON.parse(pkg.custom_permissions) : pkg.custom_permissions) : {};
                    return (
                      <div key={pkg.id} className="sa-package-card">
                        <div className="pkg-card-top">
                          <h4 className="pkg-card-name">{pkg.name}</h4>
                          {pkg.mark_package_as_popular === 1 && (
                            <span className="pkg-popular-badge">Popular</span>
                          )}
                        </div>

                        <div className="pkg-card-badges">
                          <span className={`pkg-active-badge ${pkg.is_active == 1 ? 'active' : 'inactive'}`}>
                            {pkg.is_active == 1 ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedItem(pkg);
                              setModalFormData({
                                ...pkg,
                                custom_permissions: perms,
                                is_private: pkg.is_private || 0,
                                is_one_time: pkg.is_one_time || 0,
                                enable_custom_link: pkg.enable_custom_link || 0,
                                custom_link: pkg.custom_link || '',
                                custom_link_text: pkg.custom_link_text || '',
                                businesses: pkg.businesses ? (typeof pkg.businesses === 'string' ? JSON.parse(pkg.businesses) : pkg.businesses) : []
                              });
                              setPackagesSubView('edit');
                            }}
                            className="pkg-icon-btn"
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeletePackage(pkg.id)}
                            className="pkg-icon-btn danger"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>

                        <div className="pkg-features-list">
                          <div className="pkg-feature-item">{pkg.location_count == 0 ? 'Unlimited Business Locations' : `${pkg.location_count} Business Locations`}</div>
                          <div className="pkg-feature-item">{pkg.user_count == 0 ? 'Unlimited Users' : `${pkg.user_count} Users`}</div>
                          <div className="pkg-feature-item">{pkg.product_count == 0 ? 'Unlimited Products' : `${pkg.product_count} Products`}</div>
                          <div className="pkg-feature-item">{pkg.invoice_count == 0 ? 'Unlimited Invoices' : `${pkg.invoice_count} Invoices`}</div>
                          {perms.accounting_module == 1 && <div className="pkg-feature-item">Accounting Module</div>}
                          {perms.ai_assistance_module == 1 && <div className="pkg-feature-item">AI Assistance Module</div>}
                          {perms.max_token_usage && <div className="pkg-feature-item">Max Token usage (AI Assistance)</div>}
                          {perms.connector_module == 1 && <div className="pkg-feature-item">Connector Module</div>}
                          {perms.crm_module == 1 && <div className="pkg-feature-item">Crm Module</div>}
                          {perms.essentials_module == 1 && <div className="pkg-feature-item">Essentials Module</div>}
                          {perms.manufacturing_module == 1 && <div className="pkg-feature-item">Manufacturing Module</div>}
                          {perms.project_module == 1 && <div className="pkg-feature-item">Project Module</div>}
                          {perms.repair_module == 1 && <div className="pkg-feature-item">Repair Module</div>}
                          {perms.woocommerce_module == 1 && <div className="pkg-feature-item">WooCommerce Module</div>}
                        </div>

                        <div className="pkg-price-section">
                          <span className="pkg-price">
                            ${parseFloat(pkg.price).toFixed(2)}
                            <span className="pkg-price-interval">/ {pkg.interval_count} {pkg.interval.charAt(0).toUpperCase() + pkg.interval.slice(1)}</span>
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <form onSubmit={handleAddPackageSubmit} className="pkg-form-wrapper">
                <div className="pkg-form-2col">
                  {/* Left Column */}
                  <div className="pkg-form-col">
                    <div className="pkg-field">
                      <label className="pkg-label">Name:</label>
                      <input type="text" required value={modalFormData.name || ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, name: e.target.value })}
                        className="pkg-input" />
                    </div>
                    <div className="pkg-field">
                      <label className="pkg-label">Number of Locations:</label>
                      <input type="number" min="0" required
                        value={modalFormData.location_count !== undefined && modalFormData.location_count !== null ? modalFormData.location_count : ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, location_count: e.target.value })}
                        className="pkg-input" />
                      <span className="pkg-hint">0 = infinite</span>
                    </div>
                    <div className="pkg-field">
                      <label className="pkg-label">Number of products:</label>
                      <input type="number" min="0" required
                        value={modalFormData.product_count !== undefined && modalFormData.product_count !== null ? modalFormData.product_count : ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, product_count: e.target.value })}
                        className="pkg-input" />
                      <span className="pkg-hint">0 = infinite</span>
                    </div>
                    <div className="pkg-field">
                      <label className="pkg-label">Price Interval:</label>
                      <select value={modalFormData.interval || ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, interval: e.target.value })}
                        required className="pkg-select">
                        <option value="">Please Select</option>
                        <option value="days">Days</option>
                        <option value="months">Months</option>
                        <option value="years">Years</option>
                      </select>
                    </div>
                    <div className="pkg-field">
                      <label className="pkg-label">Trial Days:</label>
                      <input type="number" min="0" required
                        value={modalFormData.trial_days !== undefined && modalFormData.trial_days !== null ? modalFormData.trial_days : ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, trial_days: e.target.value })}
                        className="pkg-input" />
                    </div>
                    <div className="pkg-field">
                      <label className="pkg-label">Sort Order:</label>
                      <input type="number" required
                        value={modalFormData.sort_order !== undefined && modalFormData.sort_order !== null ? modalFormData.sort_order : 1}
                        onChange={(e) => setModalFormData({ ...modalFormData, sort_order: e.target.value })}
                        className="pkg-input" />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="pkg-form-col">
                    <div className="pkg-field">
                      <label className="pkg-label">Package Description:</label>
                      <input type="text" required value={modalFormData.description || ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, description: e.target.value })}
                        className="pkg-input" />
                    </div>
                    <div className="pkg-field">
                      <label className="pkg-label">Number of active users:</label>
                      <input type="number" min="0" required
                        value={modalFormData.user_count !== undefined && modalFormData.user_count !== null ? modalFormData.user_count : ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, user_count: e.target.value })}
                        className="pkg-input" />
                      <span className="pkg-hint">0 = infinite</span>
                    </div>
                    <div className="pkg-field">
                      <label className="pkg-label">Number of Invoices:</label>
                      <input type="number" min="0" required
                        value={modalFormData.invoice_count !== undefined && modalFormData.invoice_count !== null ? modalFormData.invoice_count : ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, invoice_count: e.target.value })}
                        className="pkg-input" />
                      <span className="pkg-hint">0 = infinite</span>
                    </div>
                    <div className="pkg-field">
                      <label className="pkg-label">Interval Count:</label>
                      <input type="number" min="1" required
                        value={modalFormData.interval_count !== undefined && modalFormData.interval_count !== null ? modalFormData.interval_count : 1}
                        onChange={(e) => setModalFormData({ ...modalFormData, interval_count: e.target.value })}
                        className="pkg-input" />
                    </div>
                    <div className="pkg-field">
                      <label className="pkg-label">Price:</label>
                      <input type="text" required
                        value={modalFormData.price !== undefined && modalFormData.price !== null ? modalFormData.price : ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, price: e.target.value })}
                        className="pkg-input" />
                      <span className="pkg-hint">0 = Free Package</span>
                    </div>
                  </div>
                </div>

                {/* Modules & Permissions */}
                <div className="pkg-perms-section">
                  <h4 className="pkg-perms-title">Modules and Permissions</h4>
                  <div className="pkg-perms-grid">
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.is_private === 1}
                        onChange={(e) => setModalFormData({ ...modalFormData, is_private: e.target.checked ? 1 : 0 })} />
                      Private Superadmin only
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.is_one_time === 1}
                        onChange={(e) => setModalFormData({ ...modalFormData, is_one_time: e.target.checked ? 1 : 0 })} />
                      One time only subscription
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.enable_custom_link === 1}
                        onChange={(e) => setModalFormData({ ...modalFormData, enable_custom_link: e.target.checked ? 1 : 0 })} />
                      Enable custom subscription link
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.custom_permissions?.accounting_module == 1}
                        onChange={(e) => { const p = { ...modalFormData.custom_permissions }; p.accounting_module = e.target.checked ? 1 : 0; setModalFormData({ ...modalFormData, custom_permissions: p }); }} />
                      Accounting Module
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.custom_permissions?.ai_assistance_module == 1}
                        onChange={(e) => { const p = { ...modalFormData.custom_permissions }; p.ai_assistance_module = e.target.checked ? 1 : 0; setModalFormData({ ...modalFormData, custom_permissions: p }); }} />
                      AI Assistance Module
                    </label>
                    {modalFormData.custom_permissions?.ai_assistance_module == 1 && (
                      <div className="pkg-ai-token-field">
                        <label className="pkg-ai-token-label">Max Token usage (AI Assistance):</label>
                        <input type="number" className="pkg-ai-token-input"
                          value={modalFormData.custom_permissions?.max_token_usage || ''}
                          onChange={(e) => { const p = { ...modalFormData.custom_permissions }; p.max_token_usage = e.target.value; setModalFormData({ ...modalFormData, custom_permissions: p }); }} />
                      </div>
                    )}
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.custom_permissions?.connector_module == 1}
                        onChange={(e) => { const p = { ...modalFormData.custom_permissions }; p.connector_module = e.target.checked ? 1 : 0; setModalFormData({ ...modalFormData, custom_permissions: p }); }} />
                      Connector Module
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.custom_permissions?.crm_module == 1}
                        onChange={(e) => { const p = { ...modalFormData.custom_permissions }; p.crm_module = e.target.checked ? 1 : 0; setModalFormData({ ...modalFormData, custom_permissions: p }); }} />
                      Crm Module
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.custom_permissions?.essentials_module == 1}
                        onChange={(e) => { const p = { ...modalFormData.custom_permissions }; p.essentials_module = e.target.checked ? 1 : 0; setModalFormData({ ...modalFormData, custom_permissions: p }); }} />
                      Essentials Module
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.custom_permissions?.manufacturing_module == 1}
                        onChange={(e) => { const p = { ...modalFormData.custom_permissions }; p.manufacturing_module = e.target.checked ? 1 : 0; setModalFormData({ ...modalFormData, custom_permissions: p }); }} />
                      Manufacturing Module
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.custom_permissions?.project_module == 1}
                        onChange={(e) => { const p = { ...modalFormData.custom_permissions }; p.project_module = e.target.checked ? 1 : 0; setModalFormData({ ...modalFormData, custom_permissions: p }); }} />
                      Project Module
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.custom_permissions?.repair_module == 1}
                        onChange={(e) => { const p = { ...modalFormData.custom_permissions }; p.repair_module = e.target.checked ? 1 : 0; setModalFormData({ ...modalFormData, custom_permissions: p }); }} />
                      Repair Module
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.custom_permissions?.woocommerce_module == 1}
                        onChange={(e) => { const p = { ...modalFormData.custom_permissions }; p.woocommerce_module = e.target.checked ? 1 : 0; setModalFormData({ ...modalFormData, custom_permissions: p }); }} />
                      WooCommerce Module
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.is_active == 1}
                        onChange={(e) => setModalFormData({ ...modalFormData, is_active: e.target.checked ? 1 : 0 })} />
                      Is active
                    </label>
                    <label className="pkg-check-label">
                      <input type="checkbox" checked={modalFormData.mark_package_as_popular === 1}
                        onChange={(e) => setModalFormData({ ...modalFormData, mark_package_as_popular: e.target.checked ? 1 : 0 })} />
                      Mark Package as Popular
                    </label>
                  </div>
                </div>

                {modalFormData.enable_custom_link === 1 && (
                  <div className="pkg-custom-link-section">
                    <div className="pkg-field">
                      <label className="pkg-label">Custom Link:</label>
                      <input type="text" value={modalFormData.custom_link || ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, custom_link: e.target.value })}
                        className="pkg-input" />
                    </div>
                    <div className="pkg-field">
                      <label className="pkg-label">Custom Link Text:</label>
                      <input type="text" value={modalFormData.custom_link_text || ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, custom_link_text: e.target.value })}
                        className="pkg-input" />
                    </div>
                  </div>
                )}

                <div className="pkg-businesses-section">
                  <label className="pkg-label">Only for Businesses:</label>
                  <select multiple value={modalFormData.businesses || []}
                    onChange={(e) => {
                      const vals = Array.from(e.target.selectedOptions, opt => opt.value);
                      setModalFormData({ ...modalFormData, businesses: vals });
                    }}
                    className="pkg-multiselect" size="4">
                    {businesses.map(biz => (
                      <option key={biz.id} value={biz.id}>{cleanHtmlString(biz.name)}</option>
                    ))}
                  </select>
                  <span className="pkg-hint">Hold Ctrl (Cmd) to select multiple businesses.</span>
                </div>

                <div className="pkg-form-actions">
                  <button type="submit" className="pkg-save-btn">Save</button>
                  <button type="button" onClick={() => setPackagesSubView('list')} className="pkg-cancel-btn">Cancel</button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* ========================================================
            TAB: COUPONS
            ======================================================== */}
        {activeTab === 'coupons' && (
          <div className="tab-content">
            <div className="sa-action-bar">
              <h3>Coupon Management</h3>
              <button className="add-btn" onClick={() => {
                setSelectedItem(null);
                setModalFormData({
                  is_active: 1,
                  discount_type: '',
                  applied_on_packages: [],
                  applied_on_business: []
                });
                fetchCouponCreateData();
                setActiveModal('add_coupon');
              }}>
                <Plus size={16} /> Add Coupon
              </button>
            </div>

            <div className="filters-row" style={{ justifyContent: 'flex-end' }}>
              <select
                className="sa-per-page-select"
                value={couponPerPage}
                onChange={(e) => { setCouponPerPage(Number(e.target.value)); setCouponPage(1); }}
              >
                <option value={10}>10 rows</option>
                <option value={25}>25 rows</option>
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
              </select>
            </div>

            {loadingCoupons ? (
              <div className="loading-state"><RefreshCw className="animate-spin" /> Loading coupons...</div>
            ) : (
              <div className="table-responsive">
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Coupon Code</th>
                      <th>Discount</th>
                      <th>Expiry</th>
                      <th>On Packages</th>
                      <th>On Businesses</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCoupons.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="sa-text-center sa-text-muted" style={{ padding: '2rem' }}>No coupons active.</td>
                      </tr>
                    ) : (
                      paginatedCoupons.map((coupon) => (
                        <tr key={coupon.id}>
                          <td><strong className="sa-text-blue">{coupon.coupon_code}</strong></td>
                          <td>
                            {coupon.discount_type === 'percentage' ? `${parseFloat(coupon.discount)}%` : `$${parseFloat(coupon.discount).toFixed(2)}`}
                          </td>
                          <td>{coupon.expiry_date_display || 'No Expiry'}</td>
                          <td className="sa-text-muted-sm sa-truncate">{coupon.applied_on_packages_names || 'All Packages'}</td>
                          <td className="sa-text-muted-sm sa-truncate">{coupon.applied_on_business_names || 'All Businesses'}</td>
                          <td>
                            <span className={`status-badge ${coupon.is_active == 1 ? 'active' : 'inactive'}`}>
                              {coupon.is_active == 1 ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                className="sa-action-btn"
                                onClick={() => {
                                  setSelectedItem(coupon);
                                  setModalFormData({
                                    ...coupon,
                                    applied_on_packages: typeof coupon.applied_on_packages === 'string' ? JSON.parse(coupon.applied_on_packages) || [] : coupon.applied_on_packages || [],
                                    applied_on_business: typeof coupon.applied_on_business === 'string' ? JSON.parse(coupon.applied_on_business) || [] : coupon.applied_on_business || []
                                  });
                                  fetchCouponCreateData();
                                  setActiveModal('add_coupon');
                                }}
                              >
                                <Edit size={14} /> Edit
                              </button>
                              <button
                                className="sa-action-btn danger"
                                onClick={() => handleDeleteCoupon(coupon.id)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <PaginationControls
              currentPage={couponPage}
              totalItems={coupons.length}
              perPage={couponPerPage}
              onPageChange={setCouponPage}
            />
          </div>
        )}

        {/* ========================================================
            TAB: SUPERADMIN SETTINGS
            ======================================================== */}
        {activeTab === 'settings' && (
          <div className="tab-content">
            <h3><SettingsIcon size={20} /> Super Admin Configurations</h3>
            {loadingSettings || !settings ? (
              <div className="loading-state"><RefreshCw className="animate-spin" /> Loading configurations...</div>
            ) : (
              <form onSubmit={handleSaveSettings}>
                <div className="settings-tabs-container">
                  <div className="settings-tabs-nav">
                    {[
                      { id: 'application', label: 'Application Settings' },
                      { id: 'email', label: 'Email SMTP' },
                      { id: 'payment', label: 'Payment Gateways' },
                      { id: 'cron', label: 'Cron & System' }
                    ].map(st => (
                      <button
                        key={st.id}
                        type="button"
                        className={`settings-tab-btn ${settingsInnerTab === st.id ? 'active' : ''}`}
                        onClick={() => setSettingsInnerTab(st.id)}
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>

                  <div className="settings-tab-content">
                    {/* APPLICATION SETTINGS */}
                    {settingsInnerTab === 'application' && (
                      <div className="settings-section-card">
                        <div className="settings-section-title"><Building size={16} /> Application Info</div>
                        <div className="settings-form-row">
                          <div className="settings-form-group">
                            <label className="settings-form-label">APP Name</label>
                            <input
                              type="text"
                              value={settings.default_values.APP_NAME || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, APP_NAME: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                          <div className="settings-form-group">
                            <label className="settings-form-label">APP Title</label>
                            <input
                              type="text"
                              value={settings.default_values.APP_TITLE || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, APP_TITLE: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                        </div>
                        <div className="settings-form-row">
                          <div className="settings-form-group">
                            <label className="settings-form-label">Backup Disk</label>
                            <select
                              value={settings.default_values.BACKUP_DISK || 'local'}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, BACKUP_DISK: e.target.value }
                              })}
                              className="settings-form-select"
                            >
                              {Object.entries(settings.backup_disk).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* EMAIL SMTP */}
                    {settingsInnerTab === 'email' && (
                      <div className="settings-section-card">
                        <div className="settings-section-title"><Mail size={16} /> Mail Server Config (SMTP)</div>
                        <div className="settings-form-row">
                          <div className="settings-form-group">
                            <label className="settings-form-label">Mailer</label>
                            <select
                              value={settings.default_values.MAIL_MAILER || 'smtp'}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, MAIL_MAILER: e.target.value }
                              })}
                              className="settings-form-select"
                            >
                              {Object.entries(settings.mail_drivers).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                              ))}
                            </select>
                          </div>
                          <div className="settings-form-group">
                            <label className="settings-form-label">Host</label>
                            <input
                              type="text"
                              value={settings.default_values.MAIL_HOST || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, MAIL_HOST: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                        </div>
                        <div className="settings-form-row">
                          <div className="settings-form-group">
                            <label className="settings-form-label">Port</label>
                            <input
                              type="text"
                              value={settings.default_values.MAIL_PORT || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, MAIL_PORT: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                          <div className="settings-form-group">
                            <label className="settings-form-label">Encryption</label>
                            <input
                              type="text"
                              value={settings.default_values.MAIL_ENCRYPTION || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, MAIL_ENCRYPTION: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                        </div>
                        <div className="settings-form-row">
                          <div className="settings-form-group">
                            <label className="settings-form-label">Username</label>
                            <input
                              type="text"
                              value={settings.default_values.MAIL_USERNAME || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, MAIL_USERNAME: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                          <div className="settings-form-group">
                            <label className="settings-form-label">Password</label>
                            <input
                              type="password"
                              value={settings.default_values.MAIL_PASSWORD || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, MAIL_PASSWORD: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* PAYMENT GATEWAYS */}
                    {settingsInnerTab === 'payment' && (
                      <div className="settings-section-card">
                        <div className="settings-section-title"><DollarSign size={16} /> Payment Gateway Credentials</div>
                        <div className="settings-form-row">
                          <div className="settings-form-group">
                            <label className="settings-form-label">Stripe Pub Key</label>
                            <input
                              type="text"
                              value={settings.default_values.STRIPE_PUB_KEY || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, STRIPE_PUB_KEY: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                          <div className="settings-form-group">
                            <label className="settings-form-label">Stripe Secret Key</label>
                            <input
                              type="password"
                              value={settings.default_values.STRIPE_SECRET_KEY || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, STRIPE_SECRET_KEY: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                        </div>
                        <div className="settings-form-row">
                          <div className="settings-form-group">
                            <label className="settings-form-label">Paystack Public Key</label>
                            <input
                              type="text"
                              value={settings.default_values.PAYSTACK_PUBLIC_KEY || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, PAYSTACK_PUBLIC_KEY: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                          <div className="settings-form-group">
                            <label className="settings-form-label">Paystack Secret Key</label>
                            <input
                              type="password"
                              value={settings.default_values.PAYSTACK_SECRET_KEY || ''}
                              onChange={(e) => setSettings({
                                ...settings,
                                default_values: { ...settings.default_values, PAYSTACK_SECRET_KEY: e.target.value }
                              })}
                              className="settings-form-input"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* CRON & SYSTEM */}
                    {settingsInnerTab === 'cron' && (
                      <div className="settings-section-card">
                        <div className="settings-section-title"><AlertCircle size={16} /> Cron Job Command</div>
                        <pre className="sa-pre-code sa-mb-4">
                          {settings.cron_job_command}
                        </pre>
                        <div className="sa-text-muted">Superadmin Module Version: {settings.superadmin_version}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="sa-flex-between sa-mt-4">
                  <div></div>
                  <button type="submit" className="sa-btn sa-btn-success">
                    <Check size={16} /> Save Configurations
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* ========================================================
            TAB: COMMUNICATOR
            ======================================================== */}
        {activeTab === 'communicator' && (
          <div className="tab-content">
            <div className="communicator-layout">
              {/* Send box */}
              <div className="communicator-panel">
                <div className="communicator-panel-title"><Mail size={16} /> Mass Broadcast</div>
                {communicatorLoading ? (
                  <div className="loading-state"><RefreshCw className="animate-spin" /> Loading recipients...</div>
                ) : (
                  <form onSubmit={handleSendMessage}>
                    <div className="communicator-form-group">
                      <label className="communicator-form-label">Recipients (Multi-select)</label>
                      <select
                        multiple
                        value={selectedRecipients}
                        onChange={(e) => setSelectedRecipients(Array.from(e.target.selectedOptions, option => option.value))}
                        className="communicator-form-select"
                        size="6"
                      >
                        {communicatorBusinesses.map(biz => (
                          <option key={biz.id} value={biz.id}>{biz.name}</option>
                        ))}
                      </select>
                      <div className="communicator-form-hint">Hold Ctrl (Cmd) to select multiple businesses.</div>
                    </div>

                    <div className="communicator-form-group">
                      <label className="communicator-form-label">Subject</label>
                      <input
                        type="text"
                        required
                        value={messageSubject}
                        onChange={(e) => setMessageSubject(e.target.value)}
                        className="communicator-form-input"
                        placeholder="e.g. System maintenance update"
                      />
                    </div>

                    <div className="communicator-form-group">
                      <label className="communicator-form-label">Message Body</label>
                      <textarea
                        required
                        value={messageBody}
                        onChange={(e) => setMessageBody(e.target.value)}
                        className="communicator-form-textarea"
                        placeholder="Type your message here..."
                      />
                    </div>

                    <button type="submit" className="sa-btn sa-btn-success" style={{ width: '100%' }}>
                      <Mail size={16} /> Send Broadcast
                    </button>
                  </form>
                )}
              </div>

              {/* History */}
              <div className="communicator-panel">
                <div className="comm-history-header">
                  <div className="communicator-panel-title" style={{ margin: 0 }}><Mail size={16} /> Message History</div>
                  <select
                    className="sa-per-page-select"
                    value={commHistoryPerPage}
                    onChange={(e) => { setCommHistoryPerPage(Number(e.target.value)); setCommHistoryPage(1); }}
                  >
                    <option value={10}>10 rows</option>
                    <option value={25}>25 rows</option>
                    <option value={50}>50 rows</option>
                    <option value={100}>100 rows</option>
                  </select>
                </div>
                {communicatorLoading ? (
                  <div className="loading-state"><RefreshCw className="animate-spin" /> Loading history...</div>
                ) : (
                  <div className="table-responsive">
                    <table className="settings-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Subject</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCommHistory.length === 0 ? (
                          <tr>
                            <td colSpan="3" className="sa-text-center sa-text-muted" style={{ padding: '2rem' }}>No sent broadcasts found.</td>
                          </tr>
                        ) : (
                          paginatedCommHistory.map((hist, idx) => (
                            <tr key={idx} className="comm-history-row">
                              <td data-label="Date">{hist.created_at ? new Date(hist.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}</td>
                              <td data-label="Subject"><strong>{hist.subject || <span style={{ color: '#999', fontWeight: 400 }}>—</span>}</strong></td>
                              <td data-label="Message"><div style={{ whiteSpace: 'pre-line', fontSize: '0.8rem' }}>{hist.message || <span style={{ color: '#999' }}>—</span>}</div></td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                <PaginationControls
                  currentPage={commHistoryPage}
                  totalItems={communicatorHistory.length}
                  perPage={commHistoryPerPage}
                  onPageChange={setCommHistoryPage}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================
          MODALS & OVERLAYS
          ======================================================== */}
      {activeModal && (
        <div className="modal-overlay">
          <div
            className={`modal-content ${activeModal === 'add_package' || activeModal === 'add_business' || activeModal === 'view_business' ? 'modal-lg' : ''}`}
            style={activeModal === 'view_business' ? { maxWidth: '1200px', width: '95%' } : {}}
          >
            <div className="modal-header">
              <h3>
                {activeModal === 'add_business' && 'Create New Business'}
                {activeModal === 'add_subscription' && `Add Subscription for ${selectedItem?.name || ''}`}
                {activeModal === 'edit_subscription_dates' && 'Update Subscription Dates'}
                {activeModal === 'add_package' && (selectedItem ? 'Edit Package' : 'Create Package')}
                {activeModal === 'add_coupon' && (selectedItem ? 'Edit Coupon' : 'Create Coupon')}
                {activeModal === 'update_password' && `Update Password for ${selectedItem?.owner_name || ''}`}
                {activeModal === 'edit_status' && 'Update Subscription Status'}
                {activeModal === 'view_business' && 'Business Details'}
              </h3>
              <button className="close-btn" onClick={() => setActiveModal(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body" style={{ maxHeight: activeModal === 'view_business' ? '82vh' : '75vh', overflowY: 'auto' }}>
              {/* MODAL: VIEW BUSINESS */}
              {activeModal === 'view_business' && selectedItem && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  {loadingDetails ? (
                    <div style={{ padding: '4rem 0', textAlign: 'center' }}>
                      <RefreshCw className="sa-animate-spin" size={40} style={{ margin: '0 auto 1.5rem', color: '#3c8dbc' }} />
                      <div style={{ fontSize: '1.1rem', color: '#666', fontWeight: '500' }}>Fetching business details...</div>
                    </div>
                  ) : (
                    <>
                      {/* Section 1: Grid for Business and Owner Info */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>

                        {/* Business Info Card */}
                        <div style={{
                          background: '#ffffff',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          padding: '1.5rem',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                          <h4 style={{
                            margin: '0 0 1.25rem 0',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            color: '#1e293b',
                            borderBottom: '2px solid #3c8dbc',
                            paddingBottom: '0.5rem',
                            display: 'inline-block'
                          }}>
                            🏢 Business Info
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.9rem', color: '#475569' }}>
                            <div><strong style={{ color: '#1e293b' }}>Business Name:</strong> {cleanHtmlString(businessDetails?.name || selectedItem.name)}</div>
                            <div><strong style={{ color: '#1e293b' }}>Start Date:</strong> {businessDetails?.start_date || 'N/A'}</div>
                            <div><strong style={{ color: '#1e293b' }}>Timezone:</strong> {businessDetails?.time_zone || 'N/A'}</div>
                            <div><strong style={{ color: '#1e293b' }}>Currency:</strong> {businessDetails?.currency?.currency || 'N/A'} {businessDetails?.currency?.code ? `(${businessDetails.currency.code})` : ''}</div>
                            <div><strong style={{ color: '#1e293b' }}>Tax 1 Name/No:</strong> {businessDetails?.tax_label_1 || 'N/A'} / {businessDetails?.tax_number_1 || 'N/A'}</div>
                            <div><strong style={{ color: '#1e293b' }}>Website:</strong> {(businessDetails?.locations?.[0]?.website || businessDetails?.website) ? <a href={businessDetails?.locations?.[0]?.website || businessDetails?.website} target="_blank" rel="noreferrer" style={{ color: '#3c8dbc', textDecoration: 'underline' }}>{businessDetails?.locations?.[0]?.website || businessDetails?.website}</a> : 'N/A'}</div>
                            {businessDetails?.logo && (
                              <div style={{ marginTop: '0.5rem' }}>
                                <strong style={{ color: '#1e293b' }}>Logo:</strong><br />
                                <img src={`/uploads/business_logos/${businessDetails.logo}`} alt="Logo" style={{ maxHeight: '60px', marginTop: '6px', borderRadius: '6px', border: '1px solid #e2e8f0' }} />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Owner Info Card */}
                        <div style={{
                          background: '#ffffff',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          padding: '1.5rem',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                          <h4 style={{
                            margin: '0 0 1.25rem 0',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            color: '#1e293b',
                            borderBottom: '2px solid #3c8dbc',
                            paddingBottom: '0.5rem',
                            display: 'inline-block'
                          }}>
                            👑 Owner Info
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.9rem', color: '#475569' }}>
                            <div><strong style={{ color: '#1e293b' }}>Owner Name:</strong> {businessDetails?.owner ? `${businessDetails.owner.surname || ''} ${businessDetails.owner.first_name} ${businessDetails.owner.last_name || ''}` : selectedItem.owner_name}</div>
                            <div><strong style={{ color: '#1e293b' }}>Username:</strong> {businessDetails?.owner?.username || 'N/A'}</div>
                            <div><strong style={{ color: '#1e293b' }}>Email:</strong> {businessDetails?.owner?.email || selectedItem.owner_email}</div>
                            <div><strong style={{ color: '#1e293b' }}>Contact Number:</strong> {businessDetails?.owner?.contact_number || selectedItem.contact_number || 'N/A'}</div>
                          </div>
                        </div>

                      </div>

                      {/* Section 2: Locations Card */}
                      <div style={{
                        background: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        padding: '1.5rem',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}>
                        <h4 style={{
                          margin: '0 0 1rem 0',
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          color: '#1e293b',
                          borderBottom: '2px solid #3c8dbc',
                          paddingBottom: '0.5rem',
                          display: 'inline-block'
                        }}>
                          📍 Business Locations
                        </h4>
                        <div className="table-responsive">
                          <table className="settings-table">
                            <thead>
                              <tr>
                                <th>Location ID</th>
                                <th>Name</th>
                                <th>Mobile</th>
                                <th>Address</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(!businessDetails?.locations || businessDetails.locations.length === 0) ? (
                                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '1.5rem' }} className="sa-text-muted">No locations configured.</td></tr>
                              ) : (
                                businessDetails.locations.map(loc => (
                                  <tr key={loc.id}>
                                    <td>{loc.location_id || loc.id}</td>
                                    <td><strong>{loc.name}</strong></td>
                                    <td>{loc.mobile || 'N/A'}</td>
                                    <td>{[loc.landmark, loc.city, loc.state, loc.country, loc.zip_code].filter(Boolean).join(', ')}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Section 3: Subscriptions Card */}
                      <div style={{
                        background: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        padding: '1.5rem',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}>
                        <h4 style={{
                          margin: '0 0 1rem 0',
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          color: '#1e293b',
                          borderBottom: '2px solid #3c8dbc',
                          paddingBottom: '0.5rem',
                          display: 'inline-block'
                        }}>
                          💳 Subscription Records
                        </h4>
                        <div className="table-responsive">
                          <table className="settings-table">
                            <thead>
                              <tr>
                                <th>Package Name</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Price</th>
                                <th>Paid Via</th>
                                <th>Transaction ID</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(!businessDetails?.subscriptions || businessDetails.subscriptions.length === 0) ? (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '1.5rem' }} className="sa-text-muted">No subscription records found.</td></tr>
                              ) : (
                                businessDetails.subscriptions.map(sub => (
                                  <tr key={sub.id}>
                                    <td><strong>{sub.package_details?.name || sub.package?.name || 'Custom Plan'}</strong></td>
                                    <td>{sub.start_date}</td>
                                    <td>{sub.end_date}</td>
                                    <td>{formatPrice(sub.package_price)}</td>
                                    <td>{sub.paid_via}</td>
                                    <td>{sub.payment_transaction_id || 'N/A'}</td>
                                    <td>
                                      <span className={`status-badge ${sub.status === 'approved' ? 'active' : 'inactive'}`}>
                                        {sub.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Section 4: Users Card */}
                      <div style={{
                        background: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        padding: '1.5rem',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}>
                        <h4 style={{
                          margin: '0 0 1rem 0',
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          color: '#1e293b',
                          borderBottom: '2px solid #3c8dbc',
                          paddingBottom: '0.5rem',
                          display: 'inline-block'
                        }}>
                          👥 Business Users
                        </h4>
                        <div className="table-responsive">
                          <table className="settings-table">
                            <thead>
                              <tr>
                                <th>Username</th>
                                <th>Full Name</th>
                                <th>Role</th>
                                <th>Email</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {businessUsers.length === 0 ? (
                                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '1.5rem' }} className="sa-text-muted">No users found.</td></tr>
                              ) : (
                                businessUsers.map(u => (
                                  <tr key={u.id}>
                                    <td><strong>{u.username}</strong></td>
                                    <td>{u.full_name}</td>
                                    <td><span className="sa-badge-blue">{u.role}</span></td>
                                    <td>{u.email}</td>
                                    <td>
                                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                          className="sa-action-btn primary"
                                          style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                                          onClick={() => {
                                            setSelectedItem({ ...u, business_id: selectedItem.id });
                                            setModalFormData({ user_id: u.id, password: '', confirm_password: '' });
                                            setActiveModal('update_password');
                                          }}
                                        >
                                          Update Password
                                        </button>
                                        <button
                                          className="sa-action-btn success"
                                          style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                                          onClick={() => handleLoginAsUser(u)}
                                        >
                                          Login as {u.username}
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* MODAL: UPDATE PASSWORD */}
              {activeModal === 'update_password' && (
                <form onSubmit={handleUpdatePasswordSubmit}>
                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label">New Password</label>
                    <input
                      type="password"
                      required
                      value={modalFormData.password || ''}
                      onChange={(e) => setModalFormData({ ...modalFormData, password: e.target.value })}
                      className="sa-form-input"
                    />
                  </div>
                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label">Confirm Password</label>
                    <input
                      type="password"
                      required
                      value={modalFormData.confirm_password || ''}
                      onChange={(e) => setModalFormData({ ...modalFormData, confirm_password: e.target.value })}
                      className="sa-form-input"
                    />
                    {formErrors.confirm_password && (
                      <span style={{ color: '#dc3545', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.confirm_password}</span>
                    )}
                  </div>
                  <button type="submit" className="sa-btn sa-btn-success" style={{ width: '100%' }}>Update Password</button>
                </form>
              )}

              {/* MODAL: ADD BUSINESS */}
              {activeModal === 'add_business' && (
                <form onSubmit={handleAddBusinessSubmit} encType="multipart/form-data">
                  {/* Owner Section */}
                  <div style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '1rem', marginBottom: '1rem' }}>
                    <h4 style={{ fontWeight: 700, marginBottom: '0.75rem', color: '#3c8dbc', display: 'flex', alignItems: 'center', gap: '0.375rem' }}><Users size={16} /> Owner Account Details</h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Surname</label>
                        <input type="text" className="sa-form-input" style={{ fontSize: '0.875rem' }} value={modalFormData.surname || ''} onChange={(e) => setModalFormData({ ...modalFormData, surname: e.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>First Name *</label>
                        <input type="text" required className="sa-form-input" style={{ fontSize: '0.875rem' }} value={modalFormData.first_name || ''} onChange={(e) => setModalFormData({ ...modalFormData, first_name: e.target.value })} />
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Last Name</label>
                        <input type="text" className="sa-form-input" style={{ fontSize: '0.875rem' }} value={modalFormData.last_name || ''} onChange={(e) => setModalFormData({ ...modalFormData, last_name: e.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Username *</label>
                        <input type="text" required className="sa-form-input" style={{ fontSize: '0.875rem' }} value={modalFormData.username || ''} onChange={(e) => setModalFormData({ ...modalFormData, username: e.target.value })} />
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Email *</label>
                        <input type="email" required className="sa-form-input" style={{ fontSize: '0.875rem' }} value={modalFormData.email || ''} onChange={(e) => setModalFormData({ ...modalFormData, email: e.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Password *</label>
                        <input type="password" required className="sa-form-input" style={{ fontSize: '0.875rem' }} value={modalFormData.password || ''} onChange={(e) => setModalFormData({ ...modalFormData, password: e.target.value })} />
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Confirm Password *</label>
                        <input type="password" required className="sa-form-input" style={{ fontSize: '0.875rem' }} value={modalFormData.confirm_password || ''} onChange={(e) => setModalFormData({ ...modalFormData, confirm_password: e.target.value })} />
                      </div>
                    </div>
                    
                  </div>

                  {/* Business info */}
                  <div style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '1rem', marginBottom: '1rem' }}>
                    <h4 style={{ fontWeight: 700, marginBottom: '0.75rem', color: '#3c8dbc', display: 'flex', alignItems: 'center', gap: '0.375rem' }}><Building size={16} /> Business Details</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Business Name *</label>
                        <input type="text" required className="sa-form-input" style={{ fontSize: '0.875rem' }} value={modalFormData.name || ''} onChange={(e) => setModalFormData({ ...modalFormData, name: e.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Start Date</label>
                        <input type="date" className="sa-form-input" style={{ fontSize: '0.875rem' }} value={modalFormData.start_date || ''} onChange={(e) => setModalFormData({ ...modalFormData, start_date: e.target.value })} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Currency *</label>
                        <select required className="sa-form-select" style={{ fontSize: '0.875rem' }} value={modalFormData.currency_id || ''} onChange={(e) => setModalFormData({ ...modalFormData, currency_id: e.target.value })}>
                          <option value="">Select Currency</option>
                          {currenciesList && Object.entries(currenciesList).length > 0 ? (
                            Object.entries(currenciesList).map(([id, info]) => (
                              <option key={id} value={id}>{info}</option>
                            ))
                          ) : (
                            <>
                              <option value="1">USD - US Dollar</option>
                              <option value="2">EUR - Euro</option>
                              <option value="3">GBP - British Pound</option>
                              <option value="4">INR - Indian Rupee</option>
                              <option value="5">CAD - Canadian Dollar</option>
                              <option value="6">AUD - Australian Dollar</option>
                              <option value="7">AED - UAE Dirham</option>
                              <option value="8">SAR - Saudi Riyal</option>
                              <option value="9">PKR - Pakistani Rupee</option>
                              <option value="10">BDT - Bangladeshi Taka</option>
                            </>
                          )}
                        </select>
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Time Zone *</label>
                        <select required className="sa-form-select" style={{ fontSize: '0.875rem' }} value={modalFormData.time_zone || ''} onChange={(e) => setModalFormData({ ...modalFormData, time_zone: e.target.value })}>
                          <option value="">Select Time Zone</option>
                          <option value="Africa/Abidjan">Africa/Abidjan</option>
                          <option value="Africa/Accra">Africa/Accra</option>
                          <option value="Africa/Addis_Ababa">Africa/Addis_Ababa</option>
                          <option value="Africa/Algiers">Africa/Algiers</option>
                          <option value="Africa/Cairo">Africa/Cairo</option>
                          <option value="Africa/Casablanca">Africa/Casablanca</option>
                          <option value="Africa/Harare">Africa/Harare</option>
                          <option value="Africa/Johannesburg">Africa/Johannesburg</option>
                          <option value="Africa/Lagos">Africa/Lagos</option>
                          <option value="Africa/Nairobi">Africa/Nairobi</option>
                          <option value="Africa/Tunis">Africa/Tunis</option>
                          <option value="America/Anchorage">America/Anchorage</option>
                          <option value="America/Argentina/Buenos_Aires">America/Argentina/Buenos_Aires</option>
                          <option value="America/Bogota">America/Bogota</option>
                          <option value="America/Caracas">America/Caracas</option>
                          <option value="America/Chicago">America/Chicago</option>
                          <option value="America/Denver">America/Denver</option>
                          <option value="America/Edmonton">America/Edmonton</option>
                          <option value="America/Halifax">America/Halifax</option>
                          <option value="America/Lima">America/Lima</option>
                          <option value="America/Los_Angeles">America/Los_Angeles</option>
                          <option value="America/Mexico_City">America/Mexico_City</option>
                          <option value="America/New_York">America/New_York</option>
                          <option value="America/Phoenix">America/Phoenix</option>
                          <option value="America/Santiago">America/Santiago</option>
                          <option value="America/Sao_Paulo">America/Sao_Paulo</option>
                          <option value="America/Toronto">America/Toronto</option>
                          <option value="America/Vancouver">America/Vancouver</option>
                          <option value="Asia/Almaty">Asia/Almaty</option>
                          <option value="Asia/Amman">Asia/Amman</option>
                          <option value="Asia/Baghdad">Asia/Baghdad</option>
                          <option value="Asia/Baku">Asia/Baku</option>
                          <option value="Asia/Bangkok">Asia/Bangkok</option>
                          <option value="Asia/Beirut">Asia/Beirut</option>
                          <option value="Asia/Colombo">Asia/Colombo</option>
                          <option value="Asia/Dhaka">Asia/Dhaka</option>
                          <option value="Asia/Dubai">Asia/Dubai</option>
                          <option value="Asia/Hong_Kong">Asia/Hong_Kong</option>
                          <option value="Asia/Jakarta">Asia/Jakarta</option>
                          <option value="Asia/Jerusalem">Asia/Jerusalem</option>
                          <option value="Asia/Kabul">Asia/Kabul</option>
                          <option value="Asia/Karachi">Asia/Karachi</option>
                          <option value="Asia/Kathmandu">Asia/Kathmandu</option>
                          <option value="Asia/Kolkata">Asia/Kolkata</option>
                          <option value="Asia/Kuwait">Asia/Kuwait</option>
                          <option value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur</option>
                          <option value="Asia/Manila">Asia/Manila</option>
                          <option value="Asia/Muscat">Asia/Muscat</option>
                          <option value="Asia/Qatar">Asia/Qatar</option>
                          <option value="Asia/Riyadh">Asia/Riyadh</option>
                          <option value="Asia/Seoul">Asia/Seoul</option>
                          <option value="Asia/Shanghai">Asia/Shanghai</option>
                          <option value="Asia/Singapore">Asia/Singapore</option>
                          <option value="Asia/Taipei">Asia/Taipei</option>
                          <option value="Asia/Tashkent">Asia/Tashkent</option>
                          <option value="Asia/Tbilisi">Asia/Tbilisi</option>
                          <option value="Asia/Tehran">Asia/Tehran</option>
                          <option value="Asia/Tokyo">Asia/Tokyo</option>
                          <option value="Asia/Yangon">Asia/Yangon</option>
                          <option value="Atlantic/Bermuda">Atlantic/Bermuda</option>
                          <option value="Atlantic/Reykjavik">Atlantic/Reykjavik</option>
                          <option value="Australia/Adelaide">Australia/Adelaide</option>
                          <option value="Australia/Brisbane">Australia/Brisbane</option>
                          <option value="Australia/Darwin">Australia/Darwin</option>
                          <option value="Australia/Hobart">Australia/Hobart</option>
                          <option value="Australia/Melbourne">Australia/Melbourne</option>
                          <option value="Australia/Perth">Australia/Perth</option>
                          <option value="Australia/Sydney">Australia/Sydney</option>
                          <option value="Europe/Amsterdam">Europe/Amsterdam</option>
                          <option value="Europe/Athens">Europe/Athens</option>
                          <option value="Europe/Belgrade">Europe/Belgrade</option>
                          <option value="Europe/Berlin">Europe/Berlin</option>
                          <option value="Europe/Brussels">Europe/Brussels</option>
                          <option value="Europe/Bucharest">Europe/Bucharest</option>
                          <option value="Europe/Budapest">Europe/Budapest</option>
                          <option value="Europe/Copenhagen">Europe/Copenhagen</option>
                          <option value="Europe/Dublin">Europe/Dublin</option>
                          <option value="Europe/Helsinki">Europe/Helsinki</option>
                          <option value="Europe/Istanbul">Europe/Istanbul</option>
                          <option value="Europe/Kiev">Europe/Kiev</option>
                          <option value="Europe/Lisbon">Europe/Lisbon</option>
                          <option value="Europe/London">Europe/London</option>
                          <option value="Europe/Madrid">Europe/Madrid</option>
                          <option value="Europe/Moscow">Europe/Moscow</option>
                          <option value="Europe/Oslo">Europe/Oslo</option>
                          <option value="Europe/Paris">Europe/Paris</option>
                          <option value="Europe/Prague">Europe/Prague</option>
                          <option value="Europe/Rome">Europe/Rome</option>
                          <option value="Europe/Stockholm">Europe/Stockholm</option>
                          <option value="Europe/Vienna">Europe/Vienna</option>
                          <option value="Europe/Warsaw">Europe/Warsaw</option>
                          <option value="Europe/Zurich">Europe/Zurich</option>
                          <option value="Pacific/Auckland">Pacific/Auckland</option>
                          <option value="Pacific/Fiji">Pacific/Fiji</option>
                          <option value="Pacific/Honolulu">Pacific/Honolulu</option>
                          <option value="UTC">UTC</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Upload Logo</label>
                        <input type="file" accept="image/*" className="sa-form-input" style={{ fontSize: '0.875rem', padding: '4px' }} onChange={(e) => setModalFormData({ ...modalFormData, business_logo: e.target.files[0] })} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Website</label>
                        <input type="text" className="sa-form-input" style={{ fontSize: '0.875rem' }} placeholder="Website" value={modalFormData.website || ''} onChange={(e) => setModalFormData({ ...modalFormData, website: e.target.value })} />
                      </div>
                    </div>
                  </div>

                  {/* Location info */}
                  <div style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '1rem', marginBottom: '1rem' }}>
                    <h4 style={{ fontWeight: 700, marginBottom: '0.75rem', color: '#3c8dbc', display: 'flex', alignItems: 'center', gap: '0.375rem' }}><Building size={16} /> Location Details</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Country *</label>
                        <input type="text" required className="sa-form-input" style={{ fontSize: '0.875rem' }} placeholder="Country" value={modalFormData.country || ''} onChange={(e) => setModalFormData({ ...modalFormData, country: e.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>State *</label>
                        <input type="text" required className="sa-form-input" style={{ fontSize: '0.875rem' }} placeholder="State" value={modalFormData.state || ''} onChange={(e) => setModalFormData({ ...modalFormData, state: e.target.value })} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>City *</label>
                        <input type="text" required className="sa-form-input" style={{ fontSize: '0.875rem' }} placeholder="City" value={modalFormData.city || ''} onChange={(e) => setModalFormData({ ...modalFormData, city: e.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Zip Code *</label>
                        <input type="text" required className="sa-form-input" style={{ fontSize: '0.875rem' }} placeholder="Zip Code" value={modalFormData.zip_code || ''} onChange={(e) => setModalFormData({ ...modalFormData, zip_code: e.target.value })} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Landmark *</label>
                        <input type="text" required className="sa-form-input" style={{ fontSize: '0.875rem' }} placeholder="Landmark" value={modalFormData.landmark || ''} onChange={(e) => setModalFormData({ ...modalFormData, landmark: e.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Business Telephone</label>
                        <input type="text" className="sa-form-input" style={{ fontSize: '0.875rem' }} placeholder="Business contact number" value={modalFormData.mobile || ''} onChange={(e) => setModalFormData({ ...modalFormData, mobile: e.target.value })} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Alternate Number</label>
                        <input type="text" className="sa-form-input" style={{ fontSize: '0.875rem' }} placeholder="Alternate contact number" value={modalFormData.alternate_number || ''} onChange={(e) => setModalFormData({ ...modalFormData, alternate_number: e.target.value })} />
                      </div>
                    </div>
                  </div>

                  {/* Subscription */}
                  <div style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '1rem', marginBottom: '1rem' }}>
                    <h4 style={{ fontWeight: 700, marginBottom: '0.75rem', color: '#3c8dbc', display: 'flex', alignItems: 'center', gap: '0.375rem' }}><CreditCard size={16} /> Subscription</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Package</label>
                        <select className="sa-form-select" style={{ fontSize: '0.875rem' }} value={modalFormData.package_id || ''} onChange={(e) => setModalFormData({ ...modalFormData, package_id: e.target.value })}>
                          <option value="">Select Package</option>
                          {packages.map(pkg => (
                            <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Paid Via</label>
                        <select className="sa-form-select" style={{ fontSize: '0.875rem' }} value={modalFormData.paid_via || ''} onChange={(e) => setModalFormData({ ...modalFormData, paid_via: e.target.value })}>
                          <option value="">Select Payment</option>
                          <option value="offline">Offline / Manual</option>
                          <option value="stripe">Stripe</option>
                          <option value="paypal">Paypal</option>
                          <option value="razorpay">Razorpay</option>
                          <option value="paystack">Paystack</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Transaction ID</label>
                        <input type="text" className="sa-form-input" style={{ fontSize: '0.875rem' }} placeholder="e.g. TXN982348" value={modalFormData.payment_transaction_id || ''} onChange={(e) => setModalFormData({ ...modalFormData, payment_transaction_id: e.target.value })} />
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="sa-btn sa-btn-success" style={{ width: '100%' }}>Create Business</button>
                </form>
              )}

              {/* MODAL: ADD SUBSCRIPTION */}
              {activeModal === 'add_subscription' && (
                <form onSubmit={handleAddSubscriptionSubmit}>
                  {!modalFormData.business_id && (
                    <div className="sa-form-group sa-mb-4">
                      <label className="sa-form-label">Select Business</label>
                      <select
                        required
                        value={modalFormData.business_id || ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, business_id: e.target.value })}
                        className="sa-form-select"
                      >
                        <option value="">-- Select Business --</option>
                        {businesses.map(b => (
                          <option key={b.id} value={b.id}>{cleanHtmlString(b.name)}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label">Select Package</label>
                    <select
                      required
                      value={modalFormData.package_id || ''}
                      onChange={(e) => setModalFormData({ ...modalFormData, package_id: e.target.value })}
                      className="sa-form-select"
                    >
                      <option value="">-- Select Package --</option>
                      {packages.map(pkg => (
                        <option key={pkg.id} value={pkg.id}>{pkg.name} (${parseFloat(pkg.price).toFixed(2)})</option>
                      ))}
                    </select>
                  </div>

                  <div className="sa-form-row">
                    <div className="sa-form-group">
                      <label className="sa-form-label">Paid Via *</label>
                      <select
                        required
                        value={modalFormData.paid_via || ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, paid_via: e.target.value })}
                        className="sa-form-select"
                      >
                        <option value="">-- Select Paid Via --</option>
                        <option value="offline">Offline / Manual</option>
                        <option value="stripe">Stripe</option>
                        <option value="paypal">Paypal</option>
                        <option value="razorpay">Razorpay</option>
                        <option value="paystack">Paystack</option>
                        <option value="pesapal">PesaPal</option>
                        <option value="flutterwave">Flutterwave</option>
                        <option value="myfatoorah">My Fatoorah</option>
                      </select>
                    </div>
                    <div className="sa-form-group">
                      <label className="sa-form-label">Transaction ID</label>
                      <input
                        type="text"
                        value={modalFormData.payment_transaction_id || ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, payment_transaction_id: e.target.value })}
                        className="sa-form-input"
                        placeholder="e.g. TXN982348"
                      />
                    </div>
                  </div>

                  <button type="submit" className="sa-btn sa-btn-success" style={{ width: '100%' }}>Manually Subscribe</button>
                </form>
              )}

              {/* MODAL: EDIT SUBSCRIPTION STATUS */}
              {activeModal === 'edit_status' && selectedItem && (
                <form onSubmit={handleEditStatusSubmit}>
                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label">Subscription Status</label>
                    <select
                      value={modalFormData.status || 'approved'}
                      onChange={(e) => setModalFormData({ ...modalFormData, status: e.target.value })}
                      className="sa-form-select"
                    >
                      <option value="approved">Approved</option>
                      <option value="waiting">Waiting</option>
                      <option value="declined">Declined</option>
                    </select>
                  </div>
                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label">Transaction ID</label>
                    <input
                      type="text"
                      value={modalFormData.payment_transaction_id || ''}
                      onChange={(e) => setModalFormData({ ...modalFormData, payment_transaction_id: e.target.value })}
                      className="sa-form-input"
                    />
                  </div>
                  <button type="submit" className="sa-btn sa-btn-success" style={{ width: '100%' }}>Update Status</button>
                </form>
              )}

              {/* MODAL: EDIT SUBSCRIPTION DATES */}
              {activeModal === 'edit_subscription_dates' && selectedItem && (
                <form onSubmit={handleEditDatesSubmit}>
                  <div className="sa-form-row">
                    <div className="sa-form-group">
                      <label className="sa-form-label">Start Date</label>
                      <input
                        type="date"
                        value={modalFormData.start_date || ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, start_date: e.target.value })}
                        className="sa-form-input"
                      />
                    </div>
                    <div className="sa-form-group">
                      <label className="sa-form-label">End Date</label>
                      <input
                        type="date"
                        value={modalFormData.end_date || ''}
                        onChange={(e) => setModalFormData({ ...modalFormData, end_date: e.target.value })}
                        className="sa-form-input"
                      />
                    </div>
                  </div>
                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label">Trial End Date (Optional)</label>
                    <input
                      type="date"
                      value={modalFormData.trial_end_date || ''}
                      onChange={(e) => setModalFormData({ ...modalFormData, trial_end_date: e.target.value })}
                      className="sa-form-input"
                    />
                  </div>
                  <button type="submit" className="sa-btn sa-btn-success" style={{ width: '100%' }}>Update Dates</button>
                </form>
              )}

              {/* MODAL: ADD PACKAGE */}
              {activeModal === 'add_package' && (
                <form onSubmit={handleAddPackageSubmit}>
                  <div className="sa-form-row">
                    <div className="sa-form-group">
                      <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Plan Name *</label>
                      <input type="text" required value={modalFormData.name || ''} onChange={(e) => setModalFormData({ ...modalFormData, name: e.target.value })} className="sa-form-input" style={{ fontSize: '0.875rem' }} />
                    </div>
                    <div className="sa-form-group">
                      <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Price *</label>
                      <input type="text" required value={modalFormData.price || ''} onChange={(e) => setModalFormData({ ...modalFormData, price: e.target.value })} className="sa-form-input" style={{ fontSize: '0.875rem' }} />
                    </div>
                  </div>

                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Description</label>
                    <textarea value={modalFormData.description || ''} onChange={(e) => setModalFormData({ ...modalFormData, description: e.target.value })} className="sa-form-textarea" style={{ fontSize: '0.875rem', minHeight: '60px' }} />
                  </div>

                  <div className="sa-form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <div className="sa-form-group">
                      <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Billing Interval</label>
                      <select value={modalFormData.interval || 'months'} onChange={(e) => setModalFormData({ ...modalFormData, interval: e.target.value })} className="sa-form-select" style={{ fontSize: '0.875rem' }}>
                        <option value="days">Days</option>
                        <option value="months">Months</option>
                        <option value="years">Years</option>
                      </select>
                    </div>
                    <div className="sa-form-group">
                      <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Interval Count</label>
                      <input type="number" value={modalFormData.interval_count || 1} onChange={(e) => setModalFormData({ ...modalFormData, interval_count: e.target.value })} className="sa-form-input" style={{ fontSize: '0.875rem' }} />
                    </div>
                    <div className="sa-form-group">
                      <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Trial Days</label>
                      <input type="number" value={modalFormData.trial_days || 0} onChange={(e) => setModalFormData({ ...modalFormData, trial_days: e.target.value })} className="sa-form-input" style={{ fontSize: '0.875rem' }} />
                    </div>
                  </div>

                  {/* Quotas */}
                  <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '1rem', marginTop: '1rem' }}>
                    <h4 style={{ fontWeight: 700, marginBottom: '0.5rem', fontSize: '0.75rem', textTransform: 'uppercase', color: '#777' }}>Plan Quotas (0 for Unlimited)</h4>
                    <div className="sa-form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Locations</label>
                        <input type="number" value={modalFormData.location_count || 0} onChange={(e) => setModalFormData({ ...modalFormData, location_count: e.target.value })} className="sa-form-input" style={{ fontSize: '0.875rem' }} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Users</label>
                        <input type="number" value={modalFormData.user_count || 0} onChange={(e) => setModalFormData({ ...modalFormData, user_count: e.target.value })} className="sa-form-input" style={{ fontSize: '0.875rem' }} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Products</label>
                        <input type="number" value={modalFormData.product_count || 0} onChange={(e) => setModalFormData({ ...modalFormData, product_count: e.target.value })} className="sa-form-input" style={{ fontSize: '0.875rem' }} />
                      </div>
                      <div className="sa-form-group">
                        <label className="sa-form-label" style={{ fontSize: '0.8rem' }}>Invoices</label>
                        <input type="number" value={modalFormData.invoice_count || 0} onChange={(e) => setModalFormData({ ...modalFormData, invoice_count: e.target.value })} className="sa-form-input" style={{ fontSize: '0.875rem' }} />
                      </div>
                    </div>
                  </div>

                  {/* Modules */}
                  <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '1rem', marginTop: '1rem' }}>
                    <h4 style={{ fontWeight: 700, marginBottom: '0.5rem', fontSize: '0.75rem', textTransform: 'uppercase', color: '#777' }}>Enable Sub-Modules</h4>
                    <div className="sa-form-row" style={{ fontSize: '0.8rem' }}>
                      {['crm_module', 'woocommerce_module', 'repair_module', 'essentials_module', 'manufacturing_module'].map(modName => (
                        <label key={modName} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={modalFormData.custom_permissions?.[modName] == 1}
                            onChange={(e) => {
                              const newPerms = { ...modalFormData.custom_permissions };
                              newPerms[modName] = e.target.checked ? 1 : 0;
                              setModalFormData({ ...modalFormData, custom_permissions: newPerms });
                            }}
                          />
                          {modName.replace('_module', '').toUpperCase()}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Flags */}
                  <div className="sa-form-row" style={{ borderTop: '1px solid #dee2e6', paddingTop: '1rem', marginTop: '1rem', fontSize: '0.8rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={modalFormData.is_active == 1} onChange={(e) => setModalFormData({ ...modalFormData, is_active: e.target.checked ? 1 : 0 })} />
                      Is Active
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={modalFormData.mark_package_as_popular === 1} onChange={(e) => setModalFormData({ ...modalFormData, mark_package_as_popular: e.target.checked ? 1 : 0 })} />
                      Popular Plan
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={modalFormData.is_private === 1} onChange={(e) => setModalFormData({ ...modalFormData, is_private: e.target.checked ? 1 : 0 })} />
                      Private Plan
                    </label>
                  </div>

                  <button type="submit" className="sa-btn sa-btn-success" style={{ width: '100%', marginTop: '0.5rem' }}>{selectedItem ? 'Update Plan' : 'Create Plan'}</button>
                </form>
              )}

              {/* MODAL: ADD/EDIT COUPON */}
              {activeModal === 'add_coupon' && (
                <form onSubmit={handleAddCouponSubmit}>
                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label">Coupon Code *</label>
                    <input type="text" required value={modalFormData.coupon_code || ''} onChange={(e) => setModalFormData({ ...modalFormData, coupon_code: e.target.value })} className="sa-form-input" placeholder="Coupon code" />
                  </div>

                  <div className="sa-form-row">
                    <div className="sa-form-group">
                      <label className="sa-form-label">Discount Type *</label>
                      <select required value={modalFormData.discount_type || ''} onChange={(e) => setModalFormData({ ...modalFormData, discount_type: e.target.value })} className="sa-form-select">
                        <option value="">Please Select</option>
                        <option value="fixed">Fixed</option>
                        <option value="percentage">Percentage</option>
                      </select>
                    </div>
                    <div className="sa-form-group">
                      <label className="sa-form-label">Discount *</label>
                      <input type="number" step="0.01" required value={modalFormData.discount || ''} onChange={(e) => setModalFormData({ ...modalFormData, discount: e.target.value })} className="sa-form-input" placeholder="Discount" />
                    </div>
                  </div>

                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label">Expiry Date</label>
                    <input type="date" value={modalFormData.expiry_date || ''} onChange={(e) => setModalFormData({ ...modalFormData, expiry_date: e.target.value })} className="sa-form-input" placeholder="Expiry date" />
                  </div>

                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label">Applied on Packages</label>
                    <select
                      multiple
                      value={modalFormData.applied_on_packages || []}
                      onChange={(e) => {
                        const values = Array.from(e.target.selectedOptions, opt => Number(opt.value));
                        setModalFormData({ ...modalFormData, applied_on_packages: values });
                      }}
                      className="sa-form-select"
                      size="4"
                    >
                      {couponPackages.map(pkg => (
                        <option key={pkg.id} value={pkg.id}>{cleanHtmlString(pkg.name)}</option>
                      ))}
                    </select>
                    <span className="pkg-hint">If no packages selected it can be applied to all</span>
                  </div>

                  <div className="sa-form-group sa-mb-4">
                    <label className="sa-form-label">Applied on Business</label>
                    <select
                      multiple
                      value={modalFormData.applied_on_business || []}
                      onChange={(e) => {
                        const values = Array.from(e.target.selectedOptions, opt => Number(opt.value));
                        setModalFormData({ ...modalFormData, applied_on_business: values });
                      }}
                      className="sa-form-select"
                      size="4"
                    >
                      {couponBusinesses.map(biz => (
                        <option key={biz.id} value={biz.id}>{cleanHtmlString(biz.name)}</option>
                      ))}
                    </select>
                    <span className="pkg-hint">If no business selected it can be applied to all</span>
                  </div>

                  <div className="sa-form-group sa-mb-4" style={{ fontSize: '0.8rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={modalFormData.is_active == 1} onChange={(e) => setModalFormData({ ...modalFormData, is_active: e.target.checked ? 1 : 0 })} />
                      Is Active
                    </label>
                  </div>

                  <button type="submit" className="sa-btn sa-btn-success" style={{ width: '100%' }}>{selectedItem ? 'Update Coupon' : 'Create Coupon'}</button>
                </form>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="dashboard-footer sa-mt-4">
        et-pos - V6.5 | Copyright © 2026 All rights reserved.
      </div>
    </div>
  );
};

export default SystemSettings;
